import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimeField } from "@mui/x-date-pickers/DateTimeField";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  Badge,
  Divider,
  Avatar,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  TablePagination,
  Paper,
  Checkbox,
  FormControlLabel,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  InputAdornment,
  Switch,
} from "@mui/material";

import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar
} from "recharts";

import {
  Add as AddIcon,
  Edit as EditIcon,
  ContentCopy as CloneIcon,
  Delete as DeleteIcon,
  DeleteOutline as DeleteOutlineIcon,
  Visibility as ViewIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  AttachMoney as MoneyIcon,
  EventSeat as SeatIcon,
  WorkspacePremium as PromotedIcon,
  InfoOutlined as InfoIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  Tune as TuneIcon,
  Check as CheckIcon,
  Autorenew as RefreshIcon,
  FlagOutlined as FlagIcon,
  ArrowBackIosNew as BackIcon,
} from "@mui/icons-material";
import MultipleSelectChip from "../components/ui/MultipleSelectChip.js";
import axios from "../utils/axios";
import { toast } from "react-toastify";
import LocationPicker from "components/LocationPicker.js";

const mockCasualCategories = [
  { id: 1, name: "Flat & Flatmate", icon: "🏠", count: "3,240", status: "Active", visibleToUsers: true, order: "#1" },
  { id: 2, name: "Rental House", icon: "🏡", count: "2,100", status: "Active", visibleToUsers: true, order: "#2" },
  { id: 3, name: "Trips & Travel", icon: "✈️", count: "1,890", status: "Active", visibleToUsers: true, order: "#3" },
  { id: 4, name: "Home Foods", icon: "🍱", count: "1,560", status: "Active", visibleToUsers: true, order: "#4" },
  { id: 5, name: "Games & Sports", icon: "⚽", count: "1,340", status: "Inactive", visibleToUsers: false, order: "#5" },
  { id: 6, name: "Coffee Date", icon: "☕", count: "980", status: "Active", visibleToUsers: true, order: "#6" },
  { id: 7, name: "Parties & Entertainment", icon: "🎉", count: "870", status: "Active", visibleToUsers: true, order: "#7" },
  { id: 8, name: "Buy & Sell", icon: "🛍️", count: "760", status: "Active", visibleToUsers: true, order: "#8" },
  { id: 9, name: "Offers", icon: "🏷️", count: "640", status: "Active", visibleToUsers: true, order: "#9" },
  { id: 10, name: "Pubs & Restaurants", icon: "🍻", count: "520", status: "Inactive", visibleToUsers: false, order: "#10" },
  { id: 11, name: "Local Trending", icon: "🔥", count: "430", status: "Active", visibleToUsers: true, order: "#11" },
  { id: 12, name: "Hobbies & Interests", icon: "🎨", count: "310", status: "Active", visibleToUsers: true, order: "#12" },
];

const mockFormalCategories = [
  { id: 101, name: "Full-time Jobs", icon: "💼", count: "4,120", status: "Active", visibleToUsers: true, order: "#1" },
  { id: 102, name: "Part-time / Freelance", icon: "💻", count: "2,840", status: "Active", visibleToUsers: true, order: "#2" },
  { id: 103, name: "Internships", icon: "🎓", count: "1,950", status: "Active", visibleToUsers: true, order: "#3" },
  { id: 104, name: "Consulting & Advisory", icon: "📊", count: "1,420", status: "Active", visibleToUsers: true, order: "#4" },
  { id: 105, name: "Co-Founder & Partnerships", icon: "🤝", count: "980", status: "Active", visibleToUsers: true, order: "#5" },
  { id: 106, name: "Investment & Funding", icon: "💰", count: "760", status: "Active", visibleToUsers: true, order: "#6" },
  { id: 107, name: "Corporate Projects", icon: "🏢", count: "650", status: "Active", visibleToUsers: true, order: "#7" },
  { id: 108, name: "Professional Mentorship", icon: "🧑‍🏫", count: "510", status: "Active", visibleToUsers: true, order: "#8" },
];

const mockCategories = mockCasualCategories;

const mockCurrencies = [
  { id: 1, currency: "INR", country: "India", symbol: "₹", isDefault: true, status: "Active" },
  { id: 2, currency: "USD", country: "United States", symbol: "$", isDefault: false, status: "Active" },
  { id: 3, currency: "AED", country: "UAE", symbol: "د.إ", isDefault: false, status: "Active" },
  { id: 4, currency: "EUR", country: "European Union", symbol: "€", isDefault: false, status: "Active" },
  { id: 5, currency: "GBP", country: "United Kingdom", symbol: "£", isDefault: false, status: "Inactive" },
];

const mockOpportunityTypes = [
  { id: 1, type: "Online", description: "Fully remote, no physical presence needed", status: "Active" },
  { id: 2, type: "Offline", description: "Requires physical presence at a location", status: "Active" },
  { id: 3, type: "Hybrid", description: "Mix of online and in-person interaction", status: "Active" },
  { id: 4, type: "Immediate", description: "Urgent opportunities needing quick response", status: "Active" },
  { id: 5, type: "Temporary", description: "Short-duration, time-limited opportunities", status: "Active" },
  { id: 6, type: "Permanent", description: "Long-term or ongoing engagements", status: "Active" },
  { id: 7, type: "Remote", description: "Location-independent, work from anywhere", status: "Active" },
  { id: 8, type: "In-House", description: "Work done at the client's or poster's location", status: "Active" },
  { id: 9, type: "Volunteer", description: "Non-paid, community or social benefit work", status: "Inactive" },
  { id: 10, type: "Short-Term", description: "Engagements lasting a few days to weeks", status: "Active" },
];

const mockGrowthData = [
  { name: 'Jan', value: 1100 },
  { name: 'Feb', value: 1700 },
  { name: 'Mar', value: 2300 },
  { name: 'Apr', value: 2000 },
  { name: 'May', value: 3200 },
  { name: 'Jun', value: 3700 },
];

const mockCategoryData = [
  { name: 'Consulting', value: 28, color: '#6D53F4' },
  { name: 'Flat & Flatmate', value: 22, color: '#3B82F6' },
  { name: 'Trips & Travel', value: 18, color: '#10B981' },
  { name: 'Home Foods', value: 14, color: '#F59E0B' },
  { name: 'Other', value: 18, color: '#E5E7EB' },
];

const mockConnectedData = [
  { name: 'Consulting', value: 210 },
  { name: 'Flat & Flatmate', value: 140 },
  { name: 'Mentorship', value: 120 },
  { name: 'Networking', value: 100 },
  { name: 'Trips', value: 80 },
  { name: 'Home Foods', value: 60 },
];

const mockModerationQueue = [
  { id: "MOD-001", type: "Opportunity Post", title: "Suspicious Flatmate Ad", reportedBy: "Priya M.", reason: "Misleading info", date: "2026-06-05", severity: "High", status: "Pending" },
  { id: "MOD-002", type: "Request Post", title: "Photography Request", reportedBy: "Ankit S.", reason: "Spam", date: "2026-06-05", severity: "Low", status: "Pending" },
  { id: "MOD-003", type: "Connection Request", title: "Abusive message in intro", reportedBy: "Sanya R.", reason: "Inappropriate content", date: "2026-06-04", severity: "High", status: "Escalated" },
  { id: "MOD-004", type: "Media Report", title: "Explicit image upload", reportedBy: "Ravi K.", reason: "Adult content", date: "2026-06-04", severity: "Critical", status: "Pending" },
  { id: "MOD-005", type: "User Report", title: "Fake profile complaint", reportedBy: "Meena P.", reason: "Identity fraud", date: "2026-06-03", severity: "Medium", status: "Approved" },
];

const getMediaType = (url) => {
  if (!url) return 'image';
  const ext = url.split('?')[0].split('.').pop()?.toLowerCase();
  const videoExts = ['mp4', 'mov', 'webm', 'mpeg', 'avi', 'mkv'];
  return videoExts.includes(ext) ? 'video' : 'image';
};

const getInitials = (name) => {
  if (!name) return "U";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const OpportunityManagement = () => {
  // No dual mode for offers
  const [activeTab, setActiveTab] = useState("management"); // 'management' | 'settings'
  const [opportunities, setOpportunities] = useState([]);
  const [activeSubTab, setActiveSubTab] = useState("LBK Team");
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [opportunityToDelete, setOpportunityToDelete] = useState(null);
  const [dialogType, setDialogType] = useState("add");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isEditingView, setIsEditingView] = useState(false);
  const [viewFormData, setViewFormData] = useState({});
  const [savingEvent, setSavingEvent] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationSearchLoading, setLocationSearchLoading] = useState(false);
  const locationSearchTimer = useRef(null);

  // New UI states
  const [country, setCountry] = useState("India");
  const [currency, setCurrency] = useState("INR (₹)");
  const [styleMode, setStyleMode] = useState("Casual");

  const themeColor = styleMode === "Formal" ? "#069480" : "#6D53F4";
  const themeColorHover = styleMode === "Formal" ? "#057A6A" : "#5A3FE0";

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterCurrency, setFilterCurrency] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [paidOnly, setPaidOnly] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  const [activeSettingsMenu, setActiveSettingsMenu] = useState("Moderation Queue");
  const [settingsCategoryType, setSettingsCategoryType] = useState("Casual Categories");
  const [multipleCurrencySupport, setMultipleCurrencySupport] = useState(true);

  // Opportunity Form Configuration State
  const [formConfigFields, setFormConfigFields] = useState([
    { id: 1, name: "Opportunity Title", visible: true, required: true, editable: true, action: "Show" },
    { id: 2, name: "Description", visible: true, required: true, editable: true, action: "Show" },
    { id: 3, name: "Category", visible: true, required: true, editable: true, action: "Show" },
    { id: 4, name: "Opportunity Type", visible: true, required: true, editable: true, action: "Show" },
    { id: 5, name: "Value", visible: true, required: false, editable: true, action: "Show" },
    { id: 6, name: "Currency", visible: true, required: false, editable: true, action: "Show" },
    { id: 7, name: "Location", visible: true, required: true, editable: true, action: "Show" },
    { id: 8, name: "Address", visible: true, required: false, editable: true, action: "Show" },
    { id: 9, name: "Map Location", visible: true, required: false, editable: false, action: "Show" },
    { id: 10, name: "Distance Radius", visible: true, required: false, editable: true, action: "Show" },
    { id: 11, name: "Cover Image", visible: true, required: false, editable: true, action: "Show" },
    { id: 12, name: "Gallery Images", visible: true, required: false, editable: true, action: "Show" },
    { id: 13, name: "Start Date", visible: true, required: false, editable: true, action: "Show" },
    { id: 14, name: "End Date", visible: true, required: false, editable: true, action: "Show" },
    { id: 15, name: "Expiry Date", visible: true, required: false, editable: true, action: "Show" },
    { id: 16, name: "Tags", visible: false, required: false, editable: true, action: "Show" },
    { id: 17, name: "Contact Information", visible: true, required: false, editable: true, action: "Show" },
    { id: 18, name: "Connection Requests", visible: true, required: false, editable: true, action: "Show" },
  ]);

  const toggleFormField = (id, key) => {
    setFormConfigFields(fields =>
      fields.map(field => field.id === id ? { ...field, [key]: !field[key] } : field)
    );
  };

  const setFormFieldAction = (id, action) => {
    setFormConfigFields(fields =>
      fields.map(field => field.id === id ? { ...field, action } : field)
    );
  };

  // Request Settings State
  const [requestAutoExpiryDays, setRequestAutoExpiryDays] = useState("");
  const [requestAutoExpiryEnabled, setRequestAutoExpiryEnabled] = useState(true);
  const [requestBudgetRequired, setRequestBudgetRequired] = useState(true);
  const [requestContactSharingEnabled, setRequestContactSharingEnabled] = useState(true);
  const [requestVerificationRequired, setRequestVerificationRequired] = useState(true);

  // ===== EVENT OPTIONS (dynamic) =====
  const [casualCategoryList, setCasualCategoryList] = useState(mockCasualCategories);
  const [formalCategoryList, setFormalCategoryList] = useState(mockFormalCategories);
  const [openAddCategoryDialog, setOpenAddCategoryDialog] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [newCatIcon, setNewCatIcon] = useState("🏷️");
  const [opportunityTypeList, setOpportunityTypeList] = useState(mockOpportunityTypes);
  const [openTypeDialog, setOpenTypeDialog] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeName, setTypeName] = useState("");
  const [typeDescription, setTypeDescription] = useState("");
  const [typeStatus, setTypeStatus] = useState(true);
  const [typeSubmitting, setTypeSubmitting] = useState(false);
  const [openDeleteTypeDialog, setOpenDeleteTypeDialog] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);
  const [deletingType, setDeletingType] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [eventTypeOptions, setEventTypeOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [newCategoryValue, setNewCategoryValue] = useState("");
  const [newEventTypeValue, setNewEventTypeValue] = useState("");
  const [newCurrencyValue, setNewCurrencyValue] = useState("");

  // Debounced Nominatim search for location autocomplete
  const handleLocationSearch = useCallback((inputValue) => {
    if (locationSearchTimer.current) {
      clearTimeout(locationSearchTimer.current);
    }

    if (!inputValue || inputValue.length < 3) {
      setLocationSuggestions([]);
      return;
    }

    setLocationSearchLoading(true);
    locationSearchTimer.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          format: "jsonv2",
          addressdetails: "1",
          q: inputValue,
          limit: "5",
        });
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?${params.toString()}`,
          {
            method: "GET",
            headers: { Accept: "application/json" },
          }
        );
        if (!response.ok) throw new Error("Search failed");
        const results = await response.json();
        setLocationSuggestions(
          results.map((r) => ({
            label: r.display_name,
            lat: parseFloat(r.lat),
            lng: parseFloat(r.lon),
            address: r,
          }))
        );
      } catch (err) {
        console.error("Location autocomplete error:", err);
        setLocationSuggestions([]);
      } finally {
        setLocationSearchLoading(false);
      }
    }, 300);
  }, []);

  // Fetch event options from API
  const fetchEventOptions = useCallback(async () => {
    try {
      setOptionsLoading(true);
      const [catRes, typeRes, curRes, oppTypeRes, casualCatRes, formalCatRes] = await Promise.all([
        axios.get('/opportunity-options?group=category'),
        axios.get('/opportunity-options?group=eventType'),
        axios.get('/opportunity-options?group=currency'),
        axios.get('/opportunity-types'),
        axios.get('/opportunity-categories?mode=casual'),
        axios.get('/opportunity-categories?mode=formal'),
      ]);
      setCategoryOptions(catRes.data.data || []);
      setEventTypeOptions(typeRes.data.data || []);
      setCurrencyOptions(curRes.data.data || []);
      if (oppTypeRes.data?.data && oppTypeRes.data.data.length > 0) {
        setOpportunityTypeList(oppTypeRes.data.data);
      }
      if (casualCatRes.data?.data && casualCatRes.data.data.length > 0) {
        setCasualCategoryList(casualCatRes.data.data);
      }
      if (formalCatRes.data?.data && formalCatRes.data.data.length > 0) {
        setFormalCategoryList(formalCatRes.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch event options', err);
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  const [topCategoriesData, setTopCategoriesData] = useState([]);
  const [top5CategoriesRaw, setTop5CategoriesRaw] = useState([]);
  const [topCategoriesLoading, setTopCategoriesLoading] = useState(false);

  const fetchTopCategories = useCallback(async () => {
    try {
      setTopCategoriesLoading(true);
      const res = await axios.get(`/opportunities/analytics/top-categories?mode=${styleMode.toLowerCase()}`);
      if (res.data?.success && Array.isArray(res.data?.data) && res.data.data.length > 0) {
        setTopCategoriesData(res.data.data);
      } else {
        setTopCategoriesData([]);
      }
      if (res.data?.success && Array.isArray(res.data?.top5)) {
        setTop5CategoriesRaw(res.data.top5);
      } else {
        setTop5CategoriesRaw([]);
      }
    } catch (err) {
      // Backend may be starting or empty, fallback gracefully
      setTopCategoriesData([]);
      setTop5CategoriesRaw([]);
    } finally {
      setTopCategoriesLoading(false);
    }
  }, [styleMode]);

  useEffect(() => {
    fetchTopCategories();
  }, [fetchTopCategories]);

  // Derived Top Categories data for pie chart: real API -> real loaded opportunities -> real registered categories
  const displayCategoryData = useMemo(() => {
    if (topCategoriesData && topCategoriesData.length > 0) {
      return topCategoriesData;
    }

    // Dynamic aggregation from loaded opportunities
    const counts = {};
    opportunities.forEach((op) => {
      const cats = Array.isArray(op.categories) ? op.categories : (op.category ? [op.category] : []);
      cats.forEach((c) => {
        if (c && typeof c === "string") {
          counts[c] = (counts[c] || 0) + 1;
        }
      });
    });

    const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const total = entries.reduce((s, [, count]) => s + count, 0);
    const colors = ['#6D53F4', '#3B82F6', '#10B981', '#F59E0B', '#E5E7EB'];

    if (total > 0) {
      const top4 = entries.slice(0, 4);
      const otherCount = entries.slice(4).reduce((s, [, c]) => s + c, 0);

      const result = top4.map(([name, count], i) => ({
        name,
        count,
        value: Math.round((count / total) * 100),
        color: colors[i % colors.length],
      }));

      if (otherCount > 0) {
        result.push({
          name: 'Other',
          count: otherCount,
          value: Math.round((otherCount / total) * 100),
          color: '#E5E7EB',
        });
      }
      return result;
    }

    // If 0 opportunities found yet, show registered categories for current mode
    const activeList = styleMode === 'Formal' ? formalCategoryList : casualCategoryList;
    return activeList.slice(0, 5).map((c, i) => ({
      name: c.name,
      count: c.usageCount || 0,
      value: 0,
      color: colors[i % colors.length],
    }));
  }, [topCategoriesData, opportunities, styleMode, formalCategoryList, casualCategoryList]);

  // ===== Monthly Opportunity Growth Analytics =====
  const [monthlyGrowthData, setMonthlyGrowthData] = useState([]);
  const [growthLoading, setGrowthLoading] = useState(false);

  const fetchMonthlyGrowth = useCallback(async () => {
    try {
      setGrowthLoading(true);
      const res = await axios.get(`/opportunities/analytics/monthly-growth?mode=${styleMode.toLowerCase()}`);
      if (res.data?.success && Array.isArray(res.data?.data) && res.data.data.length > 0) {
        setMonthlyGrowthData(res.data.data);
      } else {
        setMonthlyGrowthData([]);
      }
    } catch (err) {
      setMonthlyGrowthData([]);
    } finally {
      setGrowthLoading(false);
    }
  }, [styleMode]);

  useEffect(() => {
    fetchMonthlyGrowth();
  }, [fetchMonthlyGrowth]);

  // Derived Monthly Growth Data: real API -> real loaded opportunities -> calendar fallback
  const displayGrowthData = useMemo(() => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentMonthIndex = new Date().getMonth();
    const monthsToShow = Math.max(6, currentMonthIndex + 1);

    if (monthlyGrowthData && monthlyGrowthData.length > 0) {
      return monthlyGrowthData.slice(0, monthsToShow);
    }

    // Dynamic aggregation fallback from loaded opportunities in memory
    const currentYear = new Date().getFullYear();
    const countsByMonth = {};

    opportunities.forEach((op) => {
      if (op.createdAt) {
        const d = new Date(op.createdAt);
        if (d.getFullYear() === currentYear) {
          const mIndex = d.getMonth();
          countsByMonth[mIndex] = (countsByMonth[mIndex] || 0) + 1;
        }
      }
    });

    return monthNames.slice(0, monthsToShow).map((name, index) => ({
      name,
      month: index + 1,
      value: countsByMonth[index] || 0,
    }));
  }, [monthlyGrowthData, opportunities]);

  // Derived Top 5 Categories for Bar Chart (Most Connected Opportunities)
  const top5CategoriesBarData = useMemo(() => {
    const counts = {};

    // 1. First use API data if available
    if (top5CategoriesRaw && top5CategoriesRaw.length > 0) {
      top5CategoriesRaw.forEach((item) => {
        if (item.name) counts[item.name] = item.count;
      });
    } else {
      // Fallback: aggregate directly from loaded opportunities in memory for active styleMode
      opportunities.forEach((op) => {
        if (op.mode && op.mode.toLowerCase() !== styleMode.toLowerCase()) return;
        const cats = Array.isArray(op.categories) ? op.categories : (op.category ? [op.category] : []);
        cats.forEach((c) => {
          if (c && typeof c === "string") {
            counts[c] = (counts[c] || 0) + 1;
          }
        });
      });
    }

    // Sort categories by opportunity count descending
    const sorted = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, value: count }));

    const existingNames = new Set(sorted.map((c) => c.name));
    const result = [...sorted.slice(0, 5)];

    // Pad to 5 using registered categories for the current mode
    const registeredList = styleMode === "Formal" ? formalCategoryList : casualCategoryList;
    for (const cat of registeredList) {
      if (result.length >= 5) break;
      if (cat.name && !existingNames.has(cat.name)) {
        result.push({ name: cat.name, value: 0 });
        existingNames.add(cat.name);
      }
    }

    // Fallback if still under 5 (fill from mock list)
    const fallbackList = styleMode === "Formal" ? mockFormalCategories : mockCasualCategories;
    for (const cat of fallbackList) {
      if (result.length >= 5) break;
      if (cat.name && !existingNames.has(cat.name)) {
        result.push({ name: cat.name, value: 0 });
        existingNames.add(cat.name);
      }
    }

    return result.slice(0, 5);
  }, [top5CategoriesRaw, opportunities, styleMode, formalCategoryList, casualCategoryList]);

  // Derived: only active options for the Create/Edit forms & Category filter based on current mode
  const currentCategoryList = styleMode === "Formal" ? formalCategoryList : casualCategoryList;

  const activeCategoriesFromList = currentCategoryList
    .filter((c) => (c.isActive !== undefined ? c.isActive : c.status === "Active") && c.visibleToUsers !== false)
    .map((c) => c.name);

  const activeCategoriesFromAPI = categoryOptions
    .filter((o) => o.isActive)
    .map((o) => o.label || o.value);

  const availableCategories = Array.from(
    new Set([...activeCategoriesFromList, ...activeCategoriesFromAPI])
  );

  const handleToggleCategoryVisibility = async (cat) => {
    const id = cat._id || cat.id;
    const currentVisible = cat.visibleToUsers !== undefined ? cat.visibleToUsers : cat.status === "Active";
    const nextVisible = !currentVisible;

    try {
      if (cat._id) {
        await axios.put(`/opportunity-categories/${cat._id}`, {
          visibleToUsers: nextVisible,
          isActive: nextVisible,
        });
      }
      const updater = (prev) =>
        prev.map((item) => {
          if (item._id === id || item.id === id) {
            return {
              ...item,
              visibleToUsers: nextVisible,
              isActive: nextVisible,
              status: nextVisible ? "Active" : "Inactive",
            };
          }
          return item;
        });

      if (settingsCategoryType === "Formal Categories") {
        setFormalCategoryList(updater);
      } else {
        setCasualCategoryList(updater);
      }
    } catch (err) {
      console.error("Failed to update category visibility", err);
      toast.error(err.response?.data?.message || "Failed to update category visibility");
    }
  };

  const handleCreateNewCategory = async () => {
    if (!newCatName.trim()) {
      toast.error("Please enter a category name");
      return;
    }
    const isFormal = settingsCategoryType === "Formal Categories";
    const mode = isFormal ? "formal" : "casual";

    try {
      const res = await axios.post('/opportunity-categories', {
        name: newCatName.trim(),
        icon: newCatIcon.trim() || "🏷️",
        mode,
        isActive: true,
        visibleToUsers: true,
      });

      const newCat = res.data?.data || {
        id: Date.now(),
        name: newCatName.trim(),
        icon: newCatIcon.trim() || "🏷️",
        mode,
        count: "0",
        usageCount: 0,
        status: "Active",
        isActive: true,
        visibleToUsers: true,
        order: `#${(isFormal ? formalCategoryList.length : casualCategoryList.length) + 1}`,
      };

      if (isFormal) {
        setFormalCategoryList((prev) => [...prev, newCat]);
      } else {
        setCasualCategoryList((prev) => [...prev, newCat]);
      }
      toast.success(`Added "${newCatName.trim()}" to ${settingsCategoryType}`);
      setNewCatName("");
      setNewCatIcon("🏷️");
      setOpenAddCategoryDialog(false);
    } catch (err) {
      console.error("Failed to create category", err);
      toast.error(err.response?.data?.message || "Failed to create category");
    }
  };

  const activeCurrencies = currencyOptions.filter((o) => o.isActive);
  
  const listActiveTypes = opportunityTypeList
    .filter((t) => (t.isActive !== undefined ? t.isActive : t.status === "Active"))
    .map((t) => ({ _id: t._id || t.id, value: t.name || t.type || t.label || t.value, label: t.name || t.type || t.label || t.value }));

  const apiActiveTypes = eventTypeOptions
    .filter((o) => o.isActive)
    .map((o) => ({ _id: o._id, value: o.value || o.label, label: o.label || o.value }));

  const combinedTypesMap = new Map();
  [...listActiveTypes, ...apiActiveTypes].forEach((item) => {
    if (!combinedTypesMap.has(item.value)) {
      combinedTypesMap.set(item.value, item);
    }
  });

  const activeEventTypes = Array.from(combinedTypesMap.values());

  const handleToggleTypeStatus = async (item) => {
    const id = item._id || item.id;
    const currentActive = item.isActive !== undefined ? item.isActive : item.status === "Active";
    const nextActive = !currentActive;
    try {
      if (item._id) {
        await axios.put(`/opportunity-types/${item._id}`, { isActive: nextActive });
      }
      setOpportunityTypeList((prev) =>
        prev.map((t) => {
          if (t._id === id || t.id === id) {
            return {
              ...t,
              isActive: nextActive,
              status: nextActive ? "Active" : "Inactive",
            };
          }
          return t;
        })
      );
    } catch (err) {
      console.error("Failed to update status", err);
      toast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  const handleOpenDeleteType = (typeItem) => {
    setTypeToDelete(typeItem);
    setOpenDeleteTypeDialog(true);
  };

  const handleConfirmDeleteType = async () => {
    if (!typeToDelete) return;
    const id = typeToDelete._id || typeToDelete.id;
    setDeletingType(true);
    try {
      if (typeToDelete._id) {
        await axios.delete(`/opportunity-types/${typeToDelete._id}`);
      }
      setOpportunityTypeList((prev) => prev.filter((item) => item._id !== id && item.id !== id));
      toast.success("Opportunity type deleted successfully");
      setOpenDeleteTypeDialog(false);
      setTypeToDelete(null);
    } catch (err) {
      console.error("Failed to delete opportunity type", err);
      toast.error(err.response?.data?.message || "Failed to delete opportunity type");
    } finally {
      setDeletingType(false);
    }
  };

  const handleOpenAddType = () => {
    setEditingType(null);
    setTypeName("");
    setTypeDescription("");
    setTypeStatus(true);
    setOpenTypeDialog(true);
  };

  const handleOpenEditType = (typeItem) => {
    setEditingType(typeItem);
    setTypeName(typeItem.name || typeItem.label || typeItem.type || typeItem.value || "");
    setTypeDescription(typeItem.description || "");
    setTypeStatus(typeItem.isActive !== undefined ? typeItem.isActive : typeItem.status === "Active");
    setOpenTypeDialog(true);
  };

  const handleSaveType = async () => {
    if (!typeName.trim()) {
      toast.error("Please enter an opportunity type name");
      return;
    }
    setTypeSubmitting(true);
    try {
      if (editingType) {
        const id = editingType._id || editingType.id;
        if (editingType._id) {
          const res = await axios.put(`/opportunity-types/${editingType._id}`, {
            name: typeName.trim(),
            description: typeDescription.trim(),
            isActive: typeStatus,
          });
          const updated = res.data.data;
          setOpportunityTypeList((prev) =>
            prev.map((t) => (t._id === id || t.id === id ? { ...t, ...updated } : t))
          );
        } else {
          setOpportunityTypeList((prev) =>
            prev.map((t) =>
              t.id === id
                ? {
                    ...t,
                    type: typeName.trim(),
                    label: typeName.trim(),
                    value: typeName.trim(),
                    description: typeDescription.trim(),
                    isActive: typeStatus,
                    status: typeStatus ? "Active" : "Inactive",
                  }
                : t
            )
          );
        }
        toast.success("Opportunity type updated successfully");
      } else {
        const res = await axios.post('/opportunity-types', {
          name: typeName.trim(),
          description: typeDescription.trim(),
          isActive: typeStatus,
        });
        const created = res.data.data;
        setOpportunityTypeList((prev) => [...prev, created]);
        toast.success("Opportunity type added successfully");
      }
      setOpenTypeDialog(false);
    } catch (err) {
      console.error("Failed to save opportunity type", err);
      toast.error(err.response?.data?.message || "Failed to save opportunity type");
    } finally {
      setTypeSubmitting(false);
    }
  };

  // ===== Opportunity Settings CRUD helpers =====
  const handleAddOption = async (group, value, clearFn) => {
    if (!value.trim()) return;
    try {
      await axios.post('/opportunity-options', {
        group,
        label: value.trim(),
        value: value.trim(),
      });
      toast.success('Option added');
      clearFn("");
      fetchEventOptions();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add option');
    }
  };

  const handleToggleActive = async (option) => {
    try {
      await axios.put(`/opportunity-options/${option._id}`, { isActive: !option.isActive });
      fetchEventOptions();
    } catch (err) {
      toast.error('Failed to toggle status');
    }
  };

  const handleDeleteOption = async (option) => {
    try {
      await axios.delete(`/opportunity-options/${option._id}`);
      toast.success('Option deleted');
      fetchEventOptions();
    } catch (err) {
      toast.error('Failed to delete option');
    }
  };

  // for multiple categories selecion
  const handleCategoryChange = (event) => {
    const {
      target: { value },
    } = event;

    setFormData((prev) => ({
      ...prev,
      categories: typeof value === "string" ? value.split(",") : value,
    }));
  };

  // Pagination states
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(0); // MUI uses 0-based index
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    categories: [],
    eventType: "REGULAR",
    value: "",
    location: {
      country: "",
      state: "",
      city: "",
      fullAddress: "",
    },
    startDate: "",
    endDate: "",
    expiryDate: "",
    images: [],
    status: "UPCOMING",
  });

  const calculateFinalPriceString = (min, max, discount, currencyMin, currencyMax) => {
    const minVal = parseFloat(min) || 0;
    const maxVal = parseFloat(max) || 0;
    const discVal = parseFloat(discount) || 0;

    if (!min && !max) return "";

    const curr = (currencyMin && currencyMin !== "Currency") ? currencyMin : (currencyMax && currencyMax !== "Currency" ? currencyMax : "INR");

    let parts = [];
    if (min) {
      const discountedMin = minVal - (minVal * discVal) / 100;
      parts.push(`Min Price : ${curr} ${discountedMin.toFixed(2)}`);
    }
    if (max) {
      const discountedMax = maxVal - (maxVal * discVal) / 100;
      parts.push(`Max Price : ${curr} ${discountedMax.toFixed(2)}`);
    }

    return parts.join(" - ");
  };

  useEffect(() => {
    const newFinalPrice = calculateFinalPriceString(
      formData.priceRangeMin,
      formData.priceRangeMax,
      formData.discountRange,
      formData.priceCurrencyMin,
      formData.priceCurrencyMax
    );
    if (newFinalPrice !== formData.finalPrice) {
      setFormData((prev) => ({ ...prev, finalPrice: newFinalPrice }));
    }
  }, [formData.priceRangeMin, formData.priceRangeMax, formData.discountRange, formData.priceCurrencyMin, formData.priceCurrencyMax, formData.finalPrice]);

  useEffect(() => {
    if (!isEditingView) return;
    const newFinalPrice = calculateFinalPriceString(
      viewFormData.priceRangeMin,
      viewFormData.priceRangeMax,
      viewFormData.discountRange,
      viewFormData.priceCurrencyMin,
      viewFormData.priceCurrencyMax
    );
    if (newFinalPrice !== viewFormData.finalPrice) {
      setViewFormData((prev) => ({ ...prev, finalPrice: newFinalPrice }));
    }
  }, [viewFormData.priceRangeMin, viewFormData.priceRangeMax, viewFormData.discountRange, viewFormData.priceCurrencyMin, viewFormData.priceCurrencyMax, isEditingView, viewFormData.finalPrice]);

  /* ================= fetch opportunities ================= */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchOpportunities();
  }, [page, rowsPerPage, debouncedSearch, filterCategory, filterType, filterCurrency, filterLocation, filterStatus, dateRange, paidOnly, verifiedOnly, activeSubTab, styleMode]);

  useEffect(() => {
    setFilterCategory("");
  }, [styleMode]);

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
      });
      if (debouncedSearch) params.append("search", debouncedSearch);
      if (filterCategory) params.append("category", filterCategory);
      if (filterType) params.append("type", filterType);
      if (filterLocation) params.append("location", filterLocation);
      if (filterStatus) params.append("status", filterStatus);
      if (dateRange) params.append("dateRange", dateRange);
      if (paidOnly) params.append("paidOnly", "true");
      params.append("mode", styleMode.toLowerCase());

      const res = await axios.get(`/opportunities?${params.toString()}`);
      setOpportunities(res.data.opportunities || res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error("Failed to fetch opportunities");
      setOpportunities([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  /* ================= Filter Opportunities Client Side ================= */
  const filteredOpportunities = opportunities.filter((event) => {
    // 1. Search Query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const titleMatch = event.title?.toLowerCase().includes(q);
      const descMatch = event.description?.toLowerCase().includes(q);
      const posterMatch = (event.posterName || event.createdBy?.name || "").toLowerCase().includes(q);
      const cityMatch = event.location?.city?.toLowerCase().includes(q);
      const countryMatch = event.location?.country?.toLowerCase().includes(q);
      const addressMatch = event.location?.fullAddress?.toLowerCase().includes(q);
      if (!titleMatch && !descMatch && !posterMatch && !cityMatch && !countryMatch && !addressMatch) {
        return false;
      }
    }

    // 2. Category Filter
    if (filterCategory) {
      const cats = (event.categories || []).map((c) => c.toLowerCase());
      if (!cats.includes(filterCategory.toLowerCase())) {
        return false;
      }
    }

    // 3. Type Filter
    if (filterType) {
      const typeStr = (event.eventType || "").toLowerCase();
      if (typeStr !== filterType.toLowerCase()) {
        return false;
      }
    }

    // 4. Location Filter
    if (filterLocation) {
      const locStr = `${event.location?.city || ""} ${event.location?.state || ""} ${event.location?.country || ""} ${event.location?.fullAddress || ""}`.toLowerCase();
      if (!locStr.includes(filterLocation.toLowerCase())) {
        return false;
      }
    }

    // 5. Status Filter
    if (filterStatus) {
      if ((event.status || "").toUpperCase() !== filterStatus.toUpperCase()) {
        return false;
      }
    }

    // 6. Paid Only
    if (paidOnly) {
      const val = Number(event.value || event.price || 0);
      if (val <= 0) return false;
    }

    // 7. Verified Only
    if (verifiedOnly) {
      if (event.isActive === false) return false;
    }

    // 8. Date Range Filter
    if (dateRange) {
      const created = new Date(event.createdAt || event.startDate || Date.now());
      const now = new Date();
      if (dateRange === "Today") {
        const todayStart = new Date(now.setHours(0, 0, 0, 0));
        if (created < todayStart) return false;
      } else if (dateRange === "This Week") {
        const weekAgo = new Date(now.setDate(now.getDate() - 7));
        if (created < weekAgo) return false;
      } else if (dateRange === "This Month") {
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        if (created < monthStart) return false;
      } else if (dateRange === "Last 30 Days") {
        const monthAgo = new Date(now.setDate(now.getDate() - 30));
        if (created < monthAgo) return false;
      }
    }

    return true;
  });

  //Handling rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  /* ================= DIALOG HANDLING ================= */
  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      startDate: "",
      endDate: "",
      expiryDate: "",
      location: {
        city: "",
        state: "",
        country: "",
        fullAddress: "",
        lat: null,
        lng: null,
      },

      categories: [],
      priceRangeMin: "",
      priceCurrencyMin: "Currency",
      priceRangeMax: "",
      priceCurrencyMax: "Currency",
      discountRange: "",
      finalPrice: "",
      bookingLink: "",
      manageEventType: "",
      mapLocationSearch: "",
      mapSearchTrigger: "",
      mapUrl: "",
      eventType: "REGULAR",
      status: "",
      images: [],
    });
  };

  const handleOpenDialog = (type, event = null) => {
    setDialogType(type);
    setSelectedEvent(event);

    if (type === "clone" && event) {
      const parsedStartDate = dayjs(event.startDate);
      const parsedEndDate = dayjs(event.endDate);

      setFormData({
        title: event.title || "",
        description: event.description || "",
        startDate: parsedStartDate.isValid() ? parsedStartDate.toISOString() : "",
        endDate: parsedEndDate.isValid() ? parsedEndDate.toISOString() : "",
        location: {
          city: event.location?.city,
          state: event.location?.state,
          country: event.location?.country,
          fullAddress: event.location?.fullAddress || event.location?.city,
          lat: event.location?.geo?.coordinates?.[1],
          lng: event.location?.geo?.coordinates?.[0],
        },

        categories: event.categories || [],
        priceRangeMin: event.priceRangeMin || "",
        priceCurrencyMin: event.priceCurrencyMin || "Currency",
        priceRangeMax: event.priceRangeMax || "",
        priceCurrencyMax: event.priceCurrencyMax || "Currency",
        discountRange: event.discountRange || "",
        finalPrice: event.finalPrice || event.price || "",
        bookingLink: event.bookingLink || "",
        manageEventType: event.manageEventType || "",
        mapLocationSearch: event.mapLocationSearch || "",
        mapUrl: event.mapUrl || "",
        eventType: event.eventType || "REGULAR",
        status: event.status,
        images: (event.images || []).map((img, index) => ({
          ...img,
          filename: img.filename || img.url?.split('/').pop() || `image_${index}`,
          type: img.type || getMediaType(img.url),
        })),
      });
    } else {
      resetForm();
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedEvent(null);
    resetForm();
  };

  const buildViewFormData = (event) => ({
    title: event.title || "",
    description: event.description || "",
    categories: event.categories || [],
    price: event.price || "",
    startDate: event.startDate || "",
    endDate: event.endDate || "",
    bookingLink: event.bookingLink || "",
    mapUrl: event.mapUrl || "",
    discountRange: event.discountRange || "",
    finalPrice: event.finalPrice || event.price || "",
    priceRangeMin: event.priceRangeMin || "",
    priceCurrencyMin: event.priceCurrencyMin || "Currency",
    priceRangeMax: event.priceRangeMax || "",
    priceCurrencyMax: event.priceCurrencyMax || "Currency",
    manageEventType: event.manageEventType || event.eventType || "",

    status: event.status || "",
    eventType: event.eventType || "REGULAR",
    location: {
      city: event.location?.city || "",
      state: event.location?.state || "",
      country: event.location?.country || "",
      fullAddress: event.location?.fullAddress || "",
      lat: event.location?.geo?.coordinates?.[1] || null,
      lng: event.location?.geo?.coordinates?.[0] || null,
    },
    images: event.images || [],
    mapLocationSearch: event.mapLocationSearch || event.location?.fullAddress || "",
    mapSearchTrigger: "",
  });

  const handleOpenViewDialog = (event) => {
    setSelectedEvent(event);
    setIsEditingView(false);
    setViewFormData(buildViewFormData(event));
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setSelectedEvent(null);
    setIsEditingView(false);
    setViewFormData({});
  };

  const handleViewInputChange = (e) => {
    const { name, value } = e.target;
    setViewFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleViewCategoryChange = (event) => {
    const { target: { value } } = event;
    setViewFormData((prev) => ({
      ...prev,
      categories: typeof value === "string" ? value.split(",") : value,
    }));
  };

  const handleSaveEditedEvent = async () => {
    // Validation: Max price should not be less than Min price
    const minVal = parseFloat(viewFormData.priceRangeMin) || 0;
    const maxVal = parseFloat(viewFormData.priceRangeMax) || 0;
    if (viewFormData.priceRangeMin && viewFormData.priceRangeMax && maxVal < minVal) {
      toast.error("Max price cannot be less than Min price");
      return;
    }

    try {
      setSavingEvent(true);
      const payload = {
        ...viewFormData,
        price: (() => {
          const min = parseFloat(viewFormData.priceRangeMin) || 0;
          const discount = parseFloat(viewFormData.discountRange) || 0;
          return min - (min * discount) / 100;
        })(),
        finalPrice: (() => {
          const min = parseFloat(viewFormData.priceRangeMin) || 0;
          const discount = parseFloat(viewFormData.discountRange) || 0;
          return min - (min * discount) / 100;
        })(),
        location: {
          ...viewFormData.location,
          state: viewFormData.location.state || viewFormData.location.city,
          geo: {
            type: "Point",
            coordinates: [
              viewFormData.location.lng || 0,
              viewFormData.location.lat || 0,
            ],
          },
        },
      };
      await axios.put(`/opportunities/${selectedEvent._id}`, payload);
      toast.success("opportunity updated successfully");
      setIsEditingView(false);
      fetchOpportunities();
      handleCloseViewDialog();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update opportunity");
    } finally {
      setSavingEvent(false);
    }
  };

  const handleOpenDeleteDialog = (event) => {
    setOpportunityToDelete(event);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setOpportunityToDelete(null);
  };

  const handleDeleteOpportunity = async () => {
    try {
      await axios.delete(`/opportunities/${opportunityToDelete._id}`);
      toast.success("opportunity deleted successfully");
      handleCloseDeleteDialog();
      fetchOpportunities();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete opportunity");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /* ================= IMAGE UPLOAD ================= */
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setUploadingImage(true);

    try {
      const uploadData = new FormData();
      files.forEach((file) => uploadData.append("images", file));

      const res = await axios.post("/upload/multiple", uploadData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (!res.data?.success || !Array.isArray(res.data.data)) {
        throw new Error("Invalid upload response");
      }

      setFormData((prev) => {
        const newImages = res.data.data.map((img, index) => ({
          url: img.url,
          filename: img.filename || img.url?.split('/').pop() || `upload_${Date.now()}_${index}`,
          type: img.type || 'image',
          isPrimary: prev.images.length === 0 && index === 0,
        }));

        return {
          ...prev,
          images: [...prev.images, ...newImages],
        };
      });

      toast.success("Images uploaded");
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const handleRemoveImage = (index) => {
    setFormData((prev) => {
      const images = [...prev.images];
      const wasPrimary = images[index]?.isPrimary;
      images.splice(index, 1);

      if (wasPrimary && images.length) {
        images[0].isPrimary = true;
      }

      return { ...prev, images };
    });
  };

  const handleSetPrimaryImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.map((img, i) => ({
        ...img,
        isPrimary: i === index,
      })),
    }));
  };

  /* ================= Save Opportunity ================= */
  const handleSubmit = async () => {
    if (!formData.title) {
      toast.error("Please enter an Opportunity Title");
      return;
    }

    try {
      const userStr = localStorage.getItem('userInfo') || localStorage.getItem('user');
      let currentUser = null;
      try {
        currentUser = userStr ? JSON.parse(userStr) : null;
      } catch (e) {}
      const loggedInPosterName = currentUser?.name || currentUser?.userName || currentUser?.email || "";

      const payload = {
        title: formData.title,
        description: formData.description,
        posterName: loggedInPosterName,
        categories: formData.categories.length > 0 ? formData.categories : ["General"],
        eventType: formData.eventType || "REGULAR",
        opportunityType: formData.eventType || "REGULAR",
        value: formData.value ? Number(formData.value) : 0,
        price: formData.value ? Number(formData.value) : 0,
        country: formData.location?.country || "India",
        state: formData.location?.state || "",
        city: formData.location?.city || "",
        address: formData.location?.fullAddress || "",
        fullAddress: formData.location?.fullAddress || "",
        location: {
          country: formData.location?.country || "India",
          state: formData.location?.state || "",
          city: formData.location?.city || "",
          fullAddress: formData.location?.fullAddress || "",
        },
        startDate: formData.startDate || new Date().toISOString(),
        endDate: formData.endDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        expiryDate: formData.expiryDate || null,
        images: formData.images || [],
        status: formData.status || "UPCOMING",
        mode: styleMode.toLowerCase(),
      };

      await axios.post("/opportunities", payload);
      toast.success(dialogType === "clone" ? "Opportunity cloned successfully" : "Opportunity created");

      handleCloseDialog();
      fetchOpportunities();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to Save Opportunity");
    }
  };

  /* ================= HELPERS ================= */
  const getOpportunityImage = (event) => {
    if (event.images?.length) {
      const primary = event.images.find((i) => i.isPrimary);
      return primary?.url || event.images[0].url;
    }
    return null;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "UPCOMING":
        return { bg: "#E3F2FD", text: "#1976D2" };
      case "ONGOING":
        return { bg: "#E8F5E9", text: "#388E3C" };
      case "COMPLETED":
        return { bg: "#F3E5F5", text: "#7B1FA2" };
      case "CANCELLED":
        return { bg: "#FFEBEE", text: "#C62828" };
      default:
        return { bg: "#E0E0E0", text: "#616161" };
    }
  };

  const getInitials = (name) => {
    if (!name) return "UN";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().substring(0, 2);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  /* ================= UI ================= */
  /* ================= UI ================= */
  return (
    <Box sx={{ p: 4, bgcolor: "#F8F9FA", minHeight: "100vh" }}>
      {/* ================= HEADER & TOP CONTROLS ================= */}
      <Box mb={4}>
        <Typography variant="h4" fontWeight={800} color="#111827" gutterBottom>
          Nearby Opportunities Management
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={4}>
          Manage opportunity discovery, requests, categories, matching rules and user interactions.
        </Typography>

        <Grid container spacing={3} sx={{ maxWidth: 600, mb: 4 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>Country</Typography>
            <Select
              fullWidth
              size="small"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              displayEmpty
              sx={{ bgcolor: "#F3F4F6", borderRadius: "8px", "& fieldset": { border: "none" } }}
            >
              <MenuItem value="India">India</MenuItem>
              <MenuItem value="USA">USA</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>Currency</Typography>
            <Select
              fullWidth
              size="small"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              displayEmpty
              sx={{ bgcolor: "#F3F4F6", borderRadius: "8px", "& fieldset": { border: "none" } }}
            >
              <MenuItem value="INR (₹)">INR (₹)</MenuItem>
              <MenuItem value="USD ($)">USD ($)</MenuItem>
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              Currency auto-fills based on country selection.
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ display: "inline-flex", bgcolor: "#F3F4F6", borderRadius: "12px", p: 0.5, mb: 4 }}>
          <Button
            onClick={() => setStyleMode("Casual")}
            sx={{
              bgcolor: styleMode === "Casual" ? themeColor : "transparent",
              color: styleMode === "Casual" ? "#fff" : "text.secondary",
              borderRadius: "8px",
              px: 3,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: styleMode === "Casual" ? themeColorHover : "#E5E7EB" }
            }}
          >
            Casual
          </Button>
          <Button
            onClick={() => setStyleMode("Formal")}
            sx={{
              bgcolor: styleMode === "Formal" ? themeColor : "transparent",
              color: styleMode === "Formal" ? "#fff" : "text.secondary",
              borderRadius: "8px",
              px: 3,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: styleMode === "Formal" ? themeColorHover : "#E5E7EB" }
            }}
          >
            Formal
          </Button>
        </Box>
      </Box>

      {/* ================= TABS & CREATE BUTTON ================= */}
      <Box display="flex" alignItems="center" justifyContent="space-between" borderBottom="1px solid #E5E7EB" mb={4}>
        <Box display="flex" gap={4}>
          <Typography
            onClick={() => setActiveTab("management")}
            sx={{
              cursor: "pointer",
              fontWeight: activeTab === "management" ? 700 : 500,
              color: activeTab === "management" ? themeColor : "text.secondary",
              borderBottom: activeTab === "management" ? `2px solid ${themeColor}` : "2px solid transparent",
              pb: 1.5,
              fontSize: "1rem"
            }}
          >
            Opportunities Management
          </Typography>
          <Typography
            onClick={() => setActiveTab("settings")}
            sx={{
              cursor: "pointer",
              fontWeight: activeTab === "settings" ? 700 : 500,
              color: activeTab === "settings" ? themeColor : "text.secondary",
              borderBottom: activeTab === "settings" ? `2px solid ${themeColor}` : "2px solid transparent",
              pb: 1.5,
              fontSize: "1rem"
            }}
          >
            Opportunities Settings
          </Typography>
        </Box>
        {activeTab === "management" && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog("add")}
            sx={{
              bgcolor: themeColor,
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 1,
              mb: 1,
              "&:hover": { bgcolor: themeColorHover },
              boxShadow: "none"
            }}
          >
            Create Opportunity
          </Button>
        )}
      </Box>

      {/* ================= SETTINGS VIEW ================= */}
      {activeTab === "settings" && (
        <Box display="flex" flexDirection="column" gap={3}>
          <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={4} alignItems="flex-start">

            {/* Left Sidebar Menu */}
            <Paper sx={{ width: { xs: "100%", md: "260px" }, borderRadius: "16px", p: 1, boxShadow: "0 1px 4px rgba(0,0,0,0.05)", bgcolor: "#fff", flexShrink: 0 }}>
              {[
                "Category Management",
                "Opportunity Types",
                "Opportunity Form Config",
                "Settings",
                "Moderation Queue",
                "Analytics"
              ].map((menuItem) => (
                <Box
                  key={menuItem}
                  onClick={() => setActiveSettingsMenu(menuItem)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    borderRadius: "12px",
                    cursor: "pointer",
                    mb: 0.5,
                    bgcolor: activeSettingsMenu === menuItem ? (styleMode === "Formal" ? "#E6F4F2" : "#F3F0FF") : "transparent",
                    color: activeSettingsMenu === menuItem ? themeColor : "#4B5563",
                    fontWeight: activeSettingsMenu === menuItem ? 600 : 500,
                    transition: "all 0.2s",
                    "&:hover": { bgcolor: activeSettingsMenu === menuItem ? (styleMode === "Formal" ? "#E6F4F2" : "#F3F0FF") : "#F9FAFB" },
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: activeSettingsMenu === menuItem ? themeColor : "transparent",
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: "inherit" }}>
                    {menuItem}
                  </Typography>
                </Box>
              ))}
            </Paper>

            {/* Right Main Content */}
            <Paper sx={{ flex: 1, borderRadius: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", bgcolor: "#fff", width: "100%", overflow: "hidden" }}>
              {activeSettingsMenu === "Category Management" && (() => {
                const isCatFormal = settingsCategoryType === "Formal Categories";
                const catThemeColor = isCatFormal ? "#069480" : "#6D53F4";
                const catThemeColorHover = isCatFormal ? "#057A6A" : "#5A3FE0";
                const currentSettingsCategories = isCatFormal ? formalCategoryList : casualCategoryList;

                return (
                <Box>
                  {/* Header */}
                  <Box p={3} borderBottom="1px solid #F3F4F6" display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h6" fontWeight={700} color="#111827">
                        Category Management
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Manage opportunity categories visible to users ({isCatFormal ? "Formal" : "Casual"})
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenAddCategoryDialog(true)}
                      sx={{
                        bgcolor: catThemeColor,
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 600,
                        px: 3,
                        py: 1,
                        "&:hover": { bgcolor: catThemeColorHover },
                        boxShadow: "none"
                      }}
                    >
                      Add Category
                    </Button>
                  </Box>

                  {/* Body */}
                  <Box p={3}>
                    {/* Toggle Buttons */}
                    <Box sx={{ display: "inline-flex", bgcolor: "#F3F4F6", borderRadius: "12px", p: 0.5, mb: 4 }}>
                      <Button
                        onClick={() => setSettingsCategoryType("Casual Categories")}
                        sx={{
                          bgcolor: settingsCategoryType === "Casual Categories" ? "#6D53F4" : "transparent",
                          color: settingsCategoryType === "Casual Categories" ? "#fff" : "text.secondary",
                          borderRadius: "8px",
                          px: 3,
                          textTransform: "none",
                          fontWeight: 600,
                          "&:hover": { bgcolor: settingsCategoryType === "Casual Categories" ? "#5A3FE0" : "#E5E7EB" }
                        }}
                      >
                        Casual Categories
                      </Button>
                      <Button
                        onClick={() => setSettingsCategoryType("Formal Categories")}
                        sx={{
                          bgcolor: settingsCategoryType === "Formal Categories" ? "#069480" : "transparent",
                          color: settingsCategoryType === "Formal Categories" ? "#fff" : "text.secondary",
                          borderRadius: "8px",
                          px: 3,
                          textTransform: "none",
                          fontWeight: 600,
                          "&:hover": { bgcolor: settingsCategoryType === "Formal Categories" ? "#057A6A" : "#E5E7EB" }
                        }}
                      >
                        Formal Categories
                      </Button>
                    </Box>

                    {/* Table */}
                    <TableContainer sx={{ borderTop: "1px solid #F3F4F6" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, borderBottom: "1px solid #F3F4F6", py: 2 }}>Category Name</TableCell>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, borderBottom: "1px solid #F3F4F6", py: 2 }}>Icon</TableCell>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, borderBottom: "1px solid #F3F4F6", py: 2 }}>Usage Count</TableCell>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, borderBottom: "1px solid #F3F4F6", py: 2 }}>Visible To Users</TableCell>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, borderBottom: "1px solid #F3F4F6", py: 2 }}>Status</TableCell>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, borderBottom: "1px solid #F3F4F6", py: 2 }}>Order</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {currentSettingsCategories.map((row) => {
                            const isRowActive = row.isActive !== undefined ? row.isActive : row.status === "Active";
                            const isRowVisible = row.visibleToUsers !== undefined ? row.visibleToUsers : true;
                            const rowId = row._id || row.id;
                            const usageCountDisplay = row.usageCount !== undefined ? Number(row.usageCount).toLocaleString() : (row.count || "0");
                            const orderDisplay = typeof row.order === "number" ? `#${row.order + 1}` : (row.order || "-");

                            return (
                            <TableRow key={rowId} sx={{ "&:hover": { bgcolor: "#F9FAFB" } }}>
                              <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2 }}>
                                <Typography variant="body2" fontWeight={600} color="#111827">
                                  {row.name}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2 }}>
                                <Typography fontSize="1.2rem">{row.icon}</Typography>
                              </TableCell>
                              <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2 }}>
                                <Typography variant="body2" color="#4B5563">{usageCountDisplay}</Typography>
                              </TableCell>
                              <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2 }}>
                                <Switch
                                  size="small"
                                  checked={isRowVisible && isRowActive}
                                  onChange={() => handleToggleCategoryVisibility(row)}
                                  sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: catThemeColor },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: catThemeColor },
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2 }}>
                                <Chip
                                  label={isRowActive ? "Active" : "Inactive"}
                                  size="small"
                                  sx={{
                                    bgcolor: isRowActive ? "#D1FAE5" : "#F3F4F6",
                                    color: isRowActive ? "#059669" : "#6B7280",
                                    fontWeight: 600,
                                    borderRadius: "6px",
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2 }}>
                                <Typography variant="body2" fontWeight={600} color={catThemeColor}>
                                  {orderDisplay}
                                </Typography>
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
                );
              })()}



              {activeSettingsMenu === "Opportunity Types" && (
                <Box>
                  {/* Header */}
                  <Box p={3} borderBottom="1px solid #F3F4F6" display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h6" fontWeight={700} color="#111827">
                        Opportunity Type Management
                      </Typography>
                      <Typography variant="body2" color="#6B7280" mt={0.5}>
                        Manage available opportunity types
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleOpenAddType}
                      sx={{
                        bgcolor: "#6366F1",
                        borderRadius: "10px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        px: 2.5,
                        py: 1,
                        "&:hover": { bgcolor: "#4F46E5" },
                        boxShadow: "0 4px 12px 0 rgba(99, 102, 241, 0.25)",
                      }}
                    >
                      Add Opportunity Type
                    </Button>
                  </Box>

                  {/* Body */}
                  <Box p={3}>
                    {/* Table */}
                    <TableContainer sx={{ borderRadius: "12px", border: "1px solid #F3F4F6", overflow: "hidden" }}>
                      <Table size="medium">
                        <TableHead sx={{ bgcolor: "#F9FAFB" }}>
                          <TableRow>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, borderBottom: "1px solid #F3F4F6", py: 2, px: 3 }}>Type</TableCell>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, borderBottom: "1px solid #F3F4F6", py: 2, px: 3 }}>Description</TableCell>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, borderBottom: "1px solid #F3F4F6", py: 2, px: 3 }}>Status</TableCell>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, borderBottom: "1px solid #F3F4F6", py: 2, px: 3 }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {opportunityTypeList.map((row) => {
                            const typeTitle = row.name || row.label || row.type || row.value;
                            const isActive = row.isActive !== undefined ? row.isActive : row.status === "Active";
                            const rowId = row._id || row.id;

                            return (
                              <TableRow key={rowId} sx={{ "&:hover": { bgcolor: "#FAFAFA" }, "&:last-child td, &:last-child th": { border: 0 } }}>
                                <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2.5, px: 3, verticalAlign: "middle" }}>
                                  <Typography variant="body2" fontWeight={700} color="#111827">
                                    {typeTitle}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2.5, px: 3, verticalAlign: "middle", maxWidth: 360 }}>
                                  <Typography variant="body2" color="#6B7280">
                                    {row.description || "—"}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2.5, px: 3, verticalAlign: "middle" }}>
                                  <Chip
                                    label={isActive ? "Active" : "Inactive"}
                                    size="small"
                                    sx={{
                                      bgcolor: isActive ? "#DCFCE7" : "#F3F4F6",
                                      color: isActive ? "#15803D" : "#6B7280",
                                      fontWeight: 600,
                                      fontSize: "0.75rem",
                                      borderRadius: "9999px",
                                      px: 1,
                                      height: "24px",
                                      border: "none",
                                    }}
                                  />
                                </TableCell>
                                <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2.5, px: 3, verticalAlign: "middle" }}>
                                  <Box display="flex" alignItems="center" gap={1.5}>
                                    {/* Styled toggle switch matching screenshot */}
                                    <Switch
                                      size="small"
                                      checked={isActive}
                                      onChange={() => handleToggleTypeStatus(row)}
                                      sx={{
                                        width: 38,
                                        height: 22,
                                        padding: 0,
                                        display: "flex",
                                        "& .MuiSwitch-switchBase": {
                                          padding: "2px",
                                          "&.Mui-checked": {
                                            transform: "translateX(16px)",
                                            color: "#fff",
                                            "& + .MuiSwitch-track": {
                                              backgroundColor: "#6366F1",
                                              opacity: 1,
                                              border: "none",
                                            },
                                          },
                                        },
                                        "& .MuiSwitch-thumb": {
                                          width: 18,
                                          height: 18,
                                          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.15)",
                                        },
                                        "& .MuiSwitch-track": {
                                          borderRadius: 22 / 2,
                                          opacity: 1,
                                          backgroundColor: "#E5E7EB",
                                          boxSizing: "border-box",
                                        },
                                      }}
                                    />
                                    {/* Edit button */}
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => handleOpenEditType(row)}
                                      sx={{
                                        borderRadius: "8px",
                                        borderColor: "#E5E7EB",
                                        color: "#374151",
                                        textTransform: "none",
                                        fontWeight: 500,
                                        fontSize: "0.8125rem",
                                        minWidth: "auto",
                                        px: 2,
                                        py: 0.5,
                                        bgcolor: "#FFFFFF",
                                        "&:hover": { borderColor: "#D1D5DB", bgcolor: "#F9FAFB" },
                                      }}
                                    >
                                      Edit
                                    </Button>
                                    {/* Delete button */}
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenDeleteType(row)}
                                      sx={{
                                        color: "#EF4444",
                                        borderRadius: "8px",
                                        p: 0.75,
                                        "&:hover": { bgcolor: "#FEF2F2" },
                                      }}
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              )}

              {activeSettingsMenu === "Opportunity Form Config" && (
                <Box>
                  {/* Header */}
                  <Box p={3} borderBottom="1px solid #F3F4F6">
                    <Typography variant="h6" fontWeight={700} color="#111827">
                      Opportunity Form Configuration
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Control fields users see when creating opportunities
                    </Typography>
                  </Box>

                  {/* Body */}
                  <Box p={3}>
                    <TableContainer sx={{ borderRadius: "12px", border: "1px solid #F3F4F6" }}>
                      <Table size="medium">
                        <TableHead sx={{ bgcolor: "#F9FAFB" }}>
                          <TableRow>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, borderBottom: "none", py: 2 }}>Field Name</TableCell>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, borderBottom: "none", py: 2 }}>Mandatory</TableCell>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, borderBottom: "none", py: 2 }}>Editable</TableCell>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, borderBottom: "none", py: 2 }}>Order</TableCell>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, borderBottom: "none", py: 2 }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {formConfigFields.map((row) => (
                            <TableRow key={row.id} sx={{ "&:hover": { bgcolor: "#F9FAFB" }, "&:last-child td, &:last-child th": { border: 0 } }}>
                              <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2.5 }}>
                                <Typography variant="body2" fontWeight={600} color="#111827">
                                  {row.name}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2.5 }}>
                                <Switch
                                  size="small"
                                  checked={row.required}
                                  onChange={() => toggleFormField(row.id, "required")}
                                  sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: themeColor }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: themeColor } }}
                                />
                              </TableCell>
                              <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2.5 }}>
                                <Switch
                                  size="small"
                                  checked={row.editable}
                                  onChange={() => toggleFormField(row.id, "editable")}
                                  sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: themeColor }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: themeColor } }}
                                />
                              </TableCell>
                              <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2.5 }}>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                  <Box sx={{ width: 10, display: 'flex', flexWrap: 'wrap', gap: '2px', opacity: 0.3, cursor: 'grab' }}>
                                    {[...Array(6)].map((_, i) => (
                                      <Box key={i} sx={{ width: 4, height: 4, bgcolor: 'currentColor', borderRadius: '50%' }} />
                                    ))}
                                  </Box>
                                  <Typography variant="body2" fontWeight={600} color={themeColor}>
                                    #{row.id}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2.5 }}>
                                <Box display="flex" bgcolor="#F9FAFB" borderRadius="8px" p="2px" width="fit-content" border="1px solid #E5E7EB">
                                  <Button
                                    size="small"
                                    disableElevation
                                    variant={row.action === "Show" ? "contained" : "text"}
                                    onClick={() => setFormFieldAction(row.id, "Show")}
                                    sx={{
                                      minWidth: "auto",
                                      px: 1.5,
                                      py: 0.5,
                                      borderRadius: "6px",
                                      textTransform: "none",
                                      fontWeight: 600,
                                      bgcolor: row.action === "Show" ? "#fff" : "transparent",
                                      color: row.action === "Show" ? "#374151" : "#9CA3AF",
                                      border: row.action === "Show" ? "1px solid #E5E7EB" : "1px solid transparent",
                                      "&:hover": { bgcolor: row.action === "Show" ? "#fff" : "transparent" },
                                    }}
                                  >
                                    Show
                                  </Button>
                                  <Button
                                    size="small"
                                    disableElevation
                                    variant={row.action === "Hide" ? "contained" : "text"}
                                    onClick={() => setFormFieldAction(row.id, "Hide")}
                                    sx={{
                                      minWidth: "auto",
                                      px: 1.5,
                                      py: 0.5,
                                      borderRadius: "6px",
                                      textTransform: "none",
                                      fontWeight: 600,
                                      bgcolor: row.action === "Hide" ? "#fff" : "transparent",
                                      color: row.action === "Hide" ? "#374151" : "#9CA3AF",
                                      border: row.action === "Hide" ? "1px solid #E5E7EB" : "1px solid transparent",
                                      "&:hover": { bgcolor: row.action === "Hide" ? "#fff" : "transparent" },
                                    }}
                                  >
                                    Hide
                                  </Button>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              )}

              {activeSettingsMenu === "Settings" && (
                <Box>
                  {/* Header */}
                  <Box p={3} borderBottom="1px solid #F3F4F6">
                    <Typography variant="h6" fontWeight={700} color="#111827">
                      Request Settings
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Global settings for user requests
                    </Typography>
                  </Box>

                  {/* Body */}
                  <Box p={3}>
                    <Box mb={4}>
                      <Typography variant="body2" fontWeight={600} color="#374151" mb={1}>
                        Auto Expiry (Days)
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder=""
                        value={requestAutoExpiryDays}
                        onChange={(e) => setRequestAutoExpiryDays(e.target.value)}
                        sx={{
                          bgcolor: "#fff",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            "& fieldset": { borderColor: "#E5E7EB" },
                            "&:hover fieldset": { borderColor: "#D1D5DB" },
                            "&.Mui-focused fieldset": { borderColor: themeColor },
                          },
                        }}
                      />
                    </Box>

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2} bgcolor="#F9FAFB" borderRadius="8px" border="1px solid #F3F4F6">
                          <Typography variant="body2" color="#4B5563">Auto Expiry</Typography>
                          <Switch
                            size="small"
                            checked={requestAutoExpiryEnabled}
                            onChange={(e) => setRequestAutoExpiryEnabled(e.target.checked)}
                            sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: themeColor }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: themeColor } }}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2} bgcolor="#F9FAFB" borderRadius="8px" border="1px solid #F3F4F6">
                          <Typography variant="body2" color="#4B5563">Budget Required</Typography>
                          <Switch
                            size="small"
                            checked={requestBudgetRequired}
                            onChange={(e) => setRequestBudgetRequired(e.target.checked)}
                            sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: themeColor }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: themeColor } }}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2} bgcolor="#F9FAFB" borderRadius="8px" border="1px solid #F3F4F6">
                          <Typography variant="body2" color="#4B5563">Contact Sharing Enabled</Typography>
                          <Switch
                            size="small"
                            checked={requestContactSharingEnabled}
                            onChange={(e) => setRequestContactSharingEnabled(e.target.checked)}
                            sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: themeColor }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: themeColor } }}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2} bgcolor="#F9FAFB" borderRadius="8px" border="1px solid #F3F4F6">
                          <Typography variant="body2" color="#4B5563">Verification Required</Typography>
                          <Switch
                            size="small"
                            checked={requestVerificationRequired}
                            onChange={(e) => setRequestVerificationRequired(e.target.checked)}
                            sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: themeColor }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: themeColor } }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              )}

              {activeSettingsMenu === "Analytics" && (
                <Box display="flex" flexDirection="column" gap={3}>
                  <Grid container spacing={3}>
                    {/* Opportunity & Request Growth */}
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 3, borderRadius: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", border: "1px solid #F3F4F6", height: "100%" }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={700} color="#111827">
                              Opportunity & Request Growth
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Number of opportunities posted per month
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: themeColor,
                              bgcolor: `${themeColor}15`,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: "8px",
                            }}
                          >
                            {new Date().getFullYear()}
                          </Typography>
                        </Box>
                        <Box sx={{ height: 260, width: "100%" }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={displayGrowthData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                              <defs>
                                <linearGradient id="colorGrowth" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor={themeColor} stopOpacity={0.25}/>
                                  <stop offset="95%" stopColor={themeColor} stopOpacity={0.02}/>
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#9CA3AF" }} dy={10} />
                              <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 12, fill: "#9CA3AF" }}
                                allowDecimals={false}
                                domain={[0, (dataMax) => (dataMax < 4 ? 4 : Math.ceil(dataMax * 1.25))]}
                              />
                              <RechartsTooltip
                                formatter={(val) => [`${val} ${val === 1 ? 'Opportunity' : 'Opportunities'}`, 'Posted']}
                                labelFormatter={(label) => `Month: ${label}`}
                                contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", fontSize: "0.85rem" }}
                              />
                              <Area type="monotone" dataKey="value" stroke={themeColor} strokeWidth={2.5} fillOpacity={1} fill="url(#colorGrowth)" />
                            </AreaChart>
                          </ResponsiveContainer>
                        </Box>
                      </Paper>
                    </Grid>

                    {/* Top Categories */}
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 3, borderRadius: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", border: "1px solid #F3F4F6", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <Typography variant="subtitle1" fontWeight={700} color="#111827">
                            Top Categories
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {styleMode} Mode
                          </Typography>
                        </Box>
                        {(() => {
                          const hasCategoryValues = displayCategoryData.some((d) => (d.value || d.count) > 0);
                          const chartRenderData = hasCategoryValues
                            ? displayCategoryData
                            : displayCategoryData.map((d) => ({ ...d, chartVal: 1 }));

                          return (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flex: 1, py: 0.5 }}>
                              <Box sx={{ width: 140, height: 140, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <PieChart width={140} height={140}>
                                  <Pie
                                    data={chartRenderData}
                                    cx={70}
                                    cy={70}
                                    innerRadius={38}
                                    outerRadius={65}
                                    paddingAngle={3}
                                    dataKey={hasCategoryValues ? "value" : "chartVal"}
                                    startAngle={90}
                                    endAngle={-270}
                                    stroke="none"
                                  >
                                    {chartRenderData.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={entry.color} opacity={hasCategoryValues ? 1 : 0.4} />
                                    ))}
                                  </Pie>
                                  <RechartsTooltip
                                    formatter={(val, name, item) => [
                                      hasCategoryValues ? `${item.payload.value}% (${item.payload.count || 0} posts)` : '0 posts',
                                      item.payload.name,
                                    ]}
                                    contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", fontSize: "0.8rem" }}
                                  />
                                </PieChart>
                              </Box>
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, ml: 2, flex: 1, minWidth: 0 }}>
                                {displayCategoryData.map((item, index) => (
                                  <Box key={index} display="flex" alignItems="center" justifyContent="space-between" sx={{ minWidth: 0, gap: 1 }}>
                                    <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, flex: 1 }}>
                                      <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: item.color, flexShrink: 0 }} />
                                      <Typography
                                        variant="body2"
                                        color="#4B5563"
                                        noWrap
                                        title={item.name}
                                        sx={{ fontSize: "0.82rem", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis" }}
                                      >
                                        {item.name}
                                      </Typography>
                                    </Box>
                                    <Typography variant="body2" fontWeight={700} color="#111827" sx={{ fontSize: "0.85rem", flexShrink: 0, ml: 1 }}>
                                      {item.value}%
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          );
                        })()}
                      </Paper>
                    </Grid>

                    {/* Most Connected Opportunities */}
                    <Grid item xs={12}>
                      <Paper sx={{ p: 3, borderRadius: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", border: "1px solid #F3F4F6" }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={700} color="#111827">
                              Most Connected Opportunities
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Number of opportunities in top 5 categories ({styleMode} Mode)
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: themeColor,
                              bgcolor: `${themeColor}15`,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: "8px",
                            }}
                          >
                            Top 5 Categories
                          </Typography>
                        </Box>
                        <Box sx={{ height: 280, width: "100%" }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={top5CategoriesBarData} margin={{ top: 10, right: 0, left: -20, bottom: 20 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                              <XAxis 
                                dataKey="name" 
                                axisLine={false} 
                                tickLine={false} 
                                tick={(props) => {
                                  const { x, y, payload } = props;
                                  const val = String(payload.value || '');
                                  let text1 = val;
                                  let text2 = "";
                                  if (val.length > 12 && val.includes(' ')) {
                                    const words = val.split(' ');
                                    const mid = Math.ceil(words.length / 2);
                                    text1 = words.slice(0, mid).join(' ');
                                    text2 = words.slice(mid).join(' ');
                                  }
                                  return (
                                    <g transform={`translate(${x},${y})`}>
                                      <text x={0} y={15} textAnchor="middle" fill="#9CA3AF" fontSize={11}>
                                        {text1}
                                      </text>
                                      {text2 && (
                                        <text x={0} y={29} textAnchor="middle" fill="#9CA3AF" fontSize={11}>
                                          {text2}
                                        </text>
                                      )}
                                    </g>
                                  );
                                }}
                              />
                              <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 12, fill: "#9CA3AF" }}
                                allowDecimals={false}
                                domain={[0, (dataMax) => (dataMax < 4 ? 4 : Math.ceil(dataMax * 1.25))]}
                              />
                              <RechartsTooltip
                                cursor={{ fill: "#F9FAFB" }}
                                formatter={(val) => [`${val} ${val === 1 ? 'Opportunity' : 'Opportunities'}`, 'Count']}
                                labelFormatter={(label) => `Category: ${label}`}
                                contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", fontSize: "0.85rem" }}
                              />
                              <Bar dataKey="value" fill={themeColor} radius={[6, 6, 6, 6]} barSize={80} />
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {activeSettingsMenu === "Moderation Queue" && (
                <Box>
                  {/* Header */}
                  <Box p={3} borderBottom="1px solid #F3F4F6" display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="h6" fontWeight={700} color="#111827">
                        Opportunity Posts — Moderation Queue
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        24 items pending review
                      </Typography>
                    </Box>
                    <Box display="flex" gap={2}>
                      <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        sx={{
                          borderRadius: "8px",
                          borderColor: "#E5E7EB",
                          color: "#374151",
                          textTransform: "none",
                          fontWeight: 600,
                          "&:hover": { borderColor: "#D1D5DB", bgcolor: "#F9FAFB" }
                        }}
                      >
                        Refresh
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<CheckIcon />}
                        sx={{
                          borderRadius: "8px",
                          bgcolor: "#10B981",
                          color: "#fff",
                          textTransform: "none",
                          fontWeight: 600,
                          boxShadow: "none",
                          "&:hover": { bgcolor: "#059669", boxShadow: "none" }
                        }}
                      >
                        Bulk Approve
                      </Button>
                    </Box>
                  </Box>

                  {/* Body */}
                  <Box p={3}>
                    <TableContainer sx={{ border: "1px solid #F3F4F6", borderRadius: "12px" }}>
                      <Table size="medium">
                        <TableHead sx={{ bgcolor: "#F9FAFB" }}>
                          <TableRow>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, py: 2 }}>Report ID</TableCell>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, py: 2 }}>Type</TableCell>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, py: 2 }}>Title</TableCell>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, py: 2 }}>Reported By</TableCell>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, py: 2 }}>Reason</TableCell>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, py: 2 }}>Date</TableCell>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, py: 2 }}>Severity</TableCell>
                            <TableCell sx={{ color: "#6B7280", fontWeight: 600, py: 2 }}>Status</TableCell>
                            <TableCell align="center" sx={{ color: "#6B7280", fontWeight: 600, py: 2 }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {mockModerationQueue.map((row) => (
                            <TableRow key={row.id} sx={{ "&:hover": { bgcolor: "#F9FAFB" }, "&:last-child td, &:last-child th": { border: 0 } }}>
                              <TableCell sx={{ color: "#9CA3AF", borderBottom: "1px solid #F3F4F6", py: 2.5, fontFamily: "monospace", fontSize: "0.85rem" }}>
                                {row.id}
                              </TableCell>
                              <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2.5 }}>
                                <Box sx={{ bgcolor: "#F3F4F6", color: "#6B7280", px: 1, py: 0.5, borderRadius: "6px", display: "inline-block", fontSize: "0.75rem", maxWidth: 90, lineHeight: 1.2 }}>
                                  {row.type}
                                </Box>
                              </TableCell>
                              <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2.5 }}>
                                <Typography variant="body2" fontWeight={700} color="#374151">
                                  {row.title}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2.5, color: "#6B7280", fontSize: "0.85rem" }}>
                                {row.reportedBy}
                              </TableCell>
                              <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2.5, color: "#6B7280", maxWidth: 120, fontSize: "0.85rem" }}>
                                {row.reason}
                              </TableCell>
                              <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2.5, color: "#6B7280", fontSize: "0.85rem" }}>
                                {row.date}
                              </TableCell>
                              <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2.5 }}>
                                {(() => {
                                  let color = "#6B7280", borderColor = "#E5E7EB", bgcolor = "#F9FAFB";
                                  if(row.severity === "High") { color = "#F97316"; borderColor = "#FED7AA"; bgcolor = "#FFF7ED"; }
                                  if(row.severity === "Low") { color = "#3B82F6"; borderColor = "#BFDBFE"; bgcolor = "#EFF6FF"; }
                                  if(row.severity === "Critical") { color = "#EF4444"; borderColor = "#FECACA"; bgcolor = "#FEF2F2"; }
                                  if(row.severity === "Medium") { color = "#EAB308"; borderColor = "#FEF08A"; bgcolor = "#FEFCE8"; }
                                  return (
                                    <Chip label={row.severity} size="small" sx={{ color, borderColor, bgcolor, fontWeight: 600, border: `1px solid ${borderColor}`, height: 24, fontSize: "0.75rem" }} />
                                  );
                                })()}
                              </TableCell>
                              <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2.5 }}>
                                {(() => {
                                  let color = "#6B7280", borderColor = "#E5E7EB", bgcolor = "#F9FAFB";
                                  if(row.status === "Pending") { color = "#EAB308"; borderColor = "#FEF08A"; bgcolor = "#FEFCE8"; }
                                  if(row.status === "Escalated") { color = "#F97316"; borderColor = "#FED7AA"; bgcolor = "#FFF7ED"; }
                                  if(row.status === "Approved") { color = "#10B981"; borderColor = "#BBF7D0"; bgcolor = "#F0FDF4"; }
                                  return (
                                    <Chip label={row.status} size="small" sx={{ color, borderColor, bgcolor, fontWeight: 600, border: `1px solid ${borderColor}`, height: 24, fontSize: "0.75rem" }} />
                                  );
                                })()}
                              </TableCell>
                              <TableCell align="center" sx={{ borderBottom: "1px solid #F3F4F6", py: 2.5 }}>
                                <Box display="flex" justifyContent="center" gap={0.5}>
                                  <IconButton size="small" sx={{ color: "#9CA3AF", "&:hover": { color: "#10B981", bgcolor: "#ECFDF5" } }}>
                                    <CheckIcon fontSize="small" sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  <IconButton size="small" sx={{ color: "#9CA3AF", "&:hover": { color: "#EF4444", bgcolor: "#FEF2F2" } }}>
                                    <CloseIcon fontSize="small" sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  <IconButton size="small" sx={{ color: "#9CA3AF", "&:hover": { color: themeColor, bgcolor: "#F3F4F6" } }}>
                                    <RefreshIcon fontSize="small" sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  <IconButton size="small" sx={{ color: "#9CA3AF", "&:hover": { color: "#F59E0B", bgcolor: "#FFFBEB" } }}>
                                    <FlagIcon fontSize="small" sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              )}

            </Paper>
          </Box>
        </Box>
      )}

      {/* ================= MANAGEMENT VIEW ================= */}
      {activeTab === "management" && (
        <Paper sx={{ borderRadius: "16px", p: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
          {/* Header Row */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
            <Box>
              <Typography variant="h6" fontWeight={700} color="#111827">
                Opportunity Posts
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage all user-visible opportunities
              </Typography>
            </Box>
            <Box sx={{ display: "flex", bgcolor: "#F3F4F6", borderRadius: "8px", p: 0.5 }}>
              <Button
                size="small"
                onClick={() => setActiveSubTab("LBK Team")}
                sx={{
                  bgcolor: activeSubTab === "LBK Team" ? themeColor : "transparent",
                  color: activeSubTab === "LBK Team" ? "#fff" : "text.secondary",
                  borderRadius: "6px",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2,
                  "&:hover": { bgcolor: activeSubTab === "LBK Team" ? themeColorHover : "#E5E7EB" }
                }}
              >
                LBK Team
              </Button>
              <Button
                size="small"
                onClick={() => setActiveSubTab("External")}
                sx={{
                  bgcolor: activeSubTab === "External" ? themeColor : "transparent",
                  color: activeSubTab === "External" ? "#fff" : "text.secondary",
                  borderRadius: "6px",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2,
                  "&:hover": { bgcolor: activeSubTab === "External" ? themeColorHover : "#E5E7EB" }
                }}
              >
                External
              </Button>
            </Box>
          </Box>

          {/* Filters Row 1 */}
          <Box display="flex" gap={2} mb={2} flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search by title, description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: searchQuery ? themeColor : "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery("")} edge="end">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ width: 280, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
            <Select size="small" displayEmpty value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} sx={{ borderRadius: "8px", minWidth: 120 }}>
              <MenuItem value=""><em>Category</em></MenuItem>
              {availableCategories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
            <Select size="small" displayEmpty value={filterType} onChange={(e) => setFilterType(e.target.value)} sx={{ borderRadius: "8px", minWidth: 120 }}>
              <MenuItem value=""><em>Type</em></MenuItem>
              {activeEventTypes.map(t => (
                <MenuItem key={t._id || t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
            <Select size="small" displayEmpty value={filterCurrency} onChange={(e) => setFilterCurrency(e.target.value)} sx={{ borderRadius: "8px", minWidth: 120 }}>
              <MenuItem value=""><em>Currency</em></MenuItem>
              {activeCurrencies.length > 0 ? (
                activeCurrencies.map(c => <MenuItem key={c._id} value={c.value}>{c.label}</MenuItem>)
              ) : (
                ["INR", "USD", "AED", "EUR", "GBP"].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)
              )}
            </Select>
            <Select size="small" displayEmpty value={filterLocation} onChange={(e) => setFilterLocation(e.target.value)} sx={{ borderRadius: "8px", minWidth: 120 }}>
              <MenuItem value=""><em>Location</em></MenuItem>
              {Array.from(new Set([
                "Delhi", "Mumbai", "Bangalore", "London", "India",
                ...opportunities.map(o => o.location?.city).filter(Boolean),
                ...opportunities.map(o => o.location?.country).filter(Boolean),
              ])).map(loc => (
                <MenuItem key={loc} value={loc}>{loc}</MenuItem>
              ))}
            </Select>
            <Select size="small" displayEmpty value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} sx={{ borderRadius: "8px", minWidth: 120 }}>
              <MenuItem value=""><em>Status</em></MenuItem>
              <MenuItem value="UPCOMING">UPCOMING</MenuItem>
              <MenuItem value="ONGOING">ONGOING</MenuItem>
              <MenuItem value="COMPLETED">COMPLETED</MenuItem>
              <MenuItem value="CANCELLED">CANCELLED</MenuItem>
              <MenuItem value="POSTPONED">POSTPONED</MenuItem>
            </Select>
          </Box>

          {/* Filters Row 2 */}
          <Box display="flex" gap={2} mb={3} alignItems="center" justifyContent="space-between">
            <Select size="small" displayEmpty value={dateRange} onChange={(e) => setDateRange(e.target.value)} sx={{ borderRadius: "8px", minWidth: 150 }}>
              <MenuItem value=""><em>Date Range</em></MenuItem>
              <MenuItem value="Today">Today</MenuItem>
              <MenuItem value="This Week">This Week</MenuItem>
              <MenuItem value="This Month">This Month</MenuItem>
              <MenuItem value="Last 30 Days">Last 30 Days</MenuItem>
            </Select>
            <Box display="flex" gap={3} alignItems="center">
              <FormControlLabel
                control={<Checkbox size="small" checked={paidOnly} onChange={(e) => setPaidOnly(e.target.checked)} sx={{ color: "#D1D5DB", "&.Mui-checked": { color: themeColor } }} />}
                label={<Typography variant="body2" color="text.secondary">Paid Only</Typography>}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)} sx={{ color: "#D1D5DB", "&.Mui-checked": { color: themeColor } }} />}
                label={<Typography variant="body2" color="text.secondary">Verified Only</Typography>}
              />
              {(searchQuery || filterCategory || filterType || filterCurrency || filterLocation || filterStatus || dateRange || paidOnly || verifiedOnly) && (
                <Button
                  size="small"
                  onClick={() => {
                    setSearchQuery("");
                    setFilterCategory("");
                    setFilterType("");
                    setFilterCurrency("");
                    setFilterLocation("");
                    setFilterStatus("");
                    setDateRange("");
                    setPaidOnly(false);
                    setVerifiedOnly(false);
                  }}
                  sx={{ color: "#EF4444", textTransform: "none", fontWeight: 600 }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          </Box>

          {/* Table */}
          <TableContainer sx={{ borderTop: "1px solid #F3F4F6" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#6B7280", fontWeight: 600, borderBottom: "1px solid #F3F4F6", py: 2 }}>Poster</TableCell>
                  <TableCell sx={{ color: "#6B7280", fontWeight: 600, borderBottom: "1px solid #F3F4F6", py: 2 }}>Title</TableCell>
                  <TableCell sx={{ color: "#6B7280", fontWeight: 600, borderBottom: "1px solid #F3F4F6", py: 2 }}>Category</TableCell>
                  <TableCell sx={{ color: "#6B7280", fontWeight: 600, borderBottom: "1px solid #F3F4F6", py: 2 }}>Type</TableCell>
                  <TableCell sx={{ color: "#6B7280", fontWeight: 600, borderBottom: "1px solid #F3F4F6", py: 2 }}>Value</TableCell>
                  <TableCell sx={{ color: "#6B7280", fontWeight: 600, borderBottom: "1px solid #F3F4F6", py: 2, textAlign: "right" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOpportunities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: "center", py: 4, color: "#6B7280" }}>
                      No opportunities found matching selected filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOpportunities.map((event) => (
                  <TableRow key={event._id} sx={{ "&:hover": { bgcolor: "#F9FAFB" } }}>
                    <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2 }}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ bgcolor: themeColor, width: 36, height: 36, fontSize: "0.9rem", fontWeight: 600 }}>
                          {getInitials(event.posterName || event.createdBy?.name || event.user?.name || "Unknown User")}
                        </Avatar>
                        <Typography variant="body2" fontWeight={600} color="#111827">
                          {event.posterName || event.createdBy?.name || event.user?.name || "Unknown User"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2 }}>
                      <Typography variant="body2" fontWeight={600} color="#111827" noWrap sx={{ maxWidth: 200 }}>
                        {event.title}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {event.categories?.[0] || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {event.eventType || "Offline"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2 }}>
                      <Typography variant="body2" fontWeight={600} color="#111827">
                        {event.price != null ? `₹${event.price}` : "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #F3F4F6", py: 2, textAlign: "right" }}>
                      <Box display="flex" gap={1} justifyContent="flex-end">
                        <IconButton size="small" onClick={() => handleOpenViewDialog(event)} sx={{ color: "#6B7280", "&:hover": { color: themeColor, bgcolor: "#F3F4F6" } }}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleOpenDialog("clone", event)} sx={{ color: "#6B7280", "&:hover": { color: themeColor, bgcolor: "#F3F4F6" } }}>
                          <CloneIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleOpenDeleteDialog(event)} sx={{ color: "#6B7280", "&:hover": { color: "#EF4444", bgcolor: "#FEF2F2" } }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            </Table>
          </TableContainer>

          {/* Footer Pagination */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mt={3} pt={2}>
            <Typography variant="body2" color="text.secondary">
              Showing {opportunities.length} of {total || 0} opportunities
            </Typography>
            <Box display="flex" gap={1}>
              {/* Custom pagination logic mapped to standard material UI */}
              <Button size="small" onClick={() => handleChangePage(null, Math.max(0, page - 1))} disabled={page === 0} sx={{ minWidth: 36, height: 36, p: 0, borderRadius: "8px", color: "text.secondary", border: "1px solid #E5E7EB", "&:hover": { bgcolor: "#F9FAFB" } }}>&lt;</Button>
              {[...Array(Math.min(3, Math.ceil(total / rowsPerPage)))].map((_, idx) => (
                <Button key={idx} size="small" onClick={() => handleChangePage(null, idx)} sx={{ minWidth: 36, height: 36, p: 0, borderRadius: "8px", bgcolor: page === idx ? themeColor : "transparent", color: page === idx ? "#fff" : "text.secondary", border: page === idx ? "none" : "1px solid #E5E7EB", "&:hover": { bgcolor: page === idx ? themeColorHover : "#F9FAFB" } }}>
                  {idx + 1}
                </Button>
              ))}
              {Math.ceil(total / rowsPerPage) > 3 && (
                <>
                  <Button size="small" disabled sx={{ minWidth: 36, height: 36, p: 0, borderRadius: "8px", color: "text.secondary", border: "1px solid transparent" }}>...</Button>
                  <Button size="small" onClick={() => handleChangePage(null, Math.ceil(total / rowsPerPage) - 1)} sx={{ minWidth: 36, height: 36, p: 0, borderRadius: "8px", bgcolor: page === Math.ceil(total / rowsPerPage) - 1 ? themeColor : "transparent", color: page === Math.ceil(total / rowsPerPage) - 1 ? "#fff" : "text.secondary", border: page === Math.ceil(total / rowsPerPage) - 1 ? "none" : "1px solid #E5E7EB", "&:hover": { bgcolor: page === Math.ceil(total / rowsPerPage) - 1 ? themeColorHover : "#F9FAFB" } }}>
                    {Math.ceil(total / rowsPerPage)}
                  </Button>
                </>
              )}
              <Button size="small" onClick={() => handleChangePage(null, Math.min(Math.ceil(total / rowsPerPage) - 1, page + 1))} disabled={page >= Math.ceil(total / rowsPerPage) - 1} sx={{ minWidth: 36, height: 36, p: 0, borderRadius: "8px", color: "text.secondary", border: "1px solid #E5E7EB", "&:hover": { bgcolor: "#F9FAFB" } }}>&gt;</Button>
            </Box>
          </Box>
          <Box mt={2}>
            <Box sx={{ height: 4, width: 120, bgcolor: "#D1D5DB", borderRadius: 2 }} />
          </Box>
        </Paper>
      )}      

      {/* ================= EDIT/ADD DIALOG ================= */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            bgcolor: "#fff",
            p: 0,
            boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <DialogTitle sx={{ p: 3, borderBottom: "1px solid #F3F4F6", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography variant="h6" fontWeight={700} color="#111827">
              {dialogType === "clone" ? "Clone Opportunity" : "Create Opportunity"}
            </Typography>
            <Typography variant="body2" color="#6B7280" mt={0.5}>
              Fill in the details to publish a new opportunity
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDialog} sx={{ bgcolor: "#F9FAFB", borderRadius: "8px", border: "1px solid #E5E7EB", p: 0.5 }}>
            <CloseIcon fontSize="small" color="action" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, overflowX: "hidden" }}>
          <Grid container spacing={3}>
            {/* Opportunity Title */}
            <Grid item xs={12}>
              <Typography variant="body2" color="#374151" fontWeight={600} mb={1}>
                Opportunity Title
              </Typography>
              <TextField
                fullWidth
                name="title"
                placeholder="e.g. Coffee Meetup in Central London"
                value={formData.title}
                onChange={handleInputChange}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#F9FAFB", "& fieldset": { borderColor: "#F3F4F6" } },
                }}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <Typography variant="body2" color="#374151" fontWeight={600} mb={1}>
                Description
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                name="description"
                placeholder="Describe the opportunity..."
                value={formData.description}
                onChange={handleInputChange}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#F9FAFB", "& fieldset": { borderColor: "#F3F4F6" } },
                }}
              />
            </Grid>

            {/* Category & Opportunity Type */}
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="#374151" fontWeight={600} mb={1}>
                Category
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  name="categories"
                  displayEmpty
                  value={formData.categories[0] || ""}
                  onChange={(e) => setFormData(prev => ({ ...prev, categories: [e.target.value] }))}
                  sx={{
                    borderRadius: "8px",
                    bgcolor: "#F9FAFB",
                    "& fieldset": { borderColor: "#F3F4F6" },
                  }}
                >
                  <MenuItem value="" disabled>
                    Select Category
                  </MenuItem>
                  {availableCategories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="#374151" fontWeight={600} mb={1}>
                Opportunity Type
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  name="eventType"
                  displayEmpty
                  value={formData.eventType || ""}
                  onChange={handleInputChange}
                  sx={{
                    borderRadius: "8px",
                    bgcolor: "#F9FAFB",
                    "& fieldset": { borderColor: "#F3F4F6" },
                  }}
                >
                  <MenuItem value="" disabled>
                    Select Opportunity Type
                  </MenuItem>
                  {activeEventTypes.map((t) => (
                    <MenuItem key={t._id || t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Opportunity Value */}
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="#374151" fontWeight={600} mb={1}>
                Opportunity Value
              </Typography>
              <TextField
                fullWidth
                name="value"
                placeholder="e.g. 500"
                value={formData.value || ""}
                onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#F9FAFB", "& fieldset": { borderColor: "#F3F4F6" } },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}></Grid>

            {/* Country & State */}
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="#374151" fontWeight={600} mb={1}>
                Country
              </Typography>
              <TextField
                fullWidth
                name="country"
                value={formData.location?.country || ""}
                onChange={(e) => setFormData(prev => ({ ...prev, location: { ...prev.location, country: e.target.value } }))}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#F9FAFB", "& fieldset": { borderColor: "#F3F4F6" } },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="#374151" fontWeight={600} mb={1}>
                State
              </Typography>
              <TextField
                fullWidth
                name="state"
                value={formData.location?.state || ""}
                onChange={(e) => setFormData(prev => ({ ...prev, location: { ...prev.location, state: e.target.value } }))}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#F9FAFB", "& fieldset": { borderColor: "#F3F4F6" } },
                }}
              />
            </Grid>

            {/* City & Address */}
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="#374151" fontWeight={600} mb={1}>
                City
              </Typography>
              <TextField
                fullWidth
                name="city"
                placeholder="e.g. Mumbai"
                value={formData.location?.city || ""}
                onChange={(e) => setFormData(prev => ({ ...prev, location: { ...prev.location, city: e.target.value } }))}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#F9FAFB", "& fieldset": { borderColor: "#F3F4F6" } },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="#374151" fontWeight={600} mb={1}>
                Address
              </Typography>
              <TextField
                fullWidth
                name="fullAddress"
                placeholder="Street address"
                value={formData.location?.fullAddress || ""}
                onChange={(e) => setFormData(prev => ({ ...prev, location: { ...prev.location, fullAddress: e.target.value } }))}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#F9FAFB", "& fieldset": { borderColor: "#F3F4F6" } },
                }}
              />
            </Grid>

            {/* Dates */}
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="#374151" fontWeight={600} mb={1}>
                Start Date
              </Typography>
              <TextField
                fullWidth
                type="date"
                value={formData.startDate ? formData.startDate.substring(0, 10) : ""}
                onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value ? new Date(e.target.value).toISOString() : "" }))}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#F9FAFB", "& fieldset": { borderColor: "#F3F4F6" } },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="#374151" fontWeight={600} mb={1}>
                End Date
              </Typography>
              <TextField
                fullWidth
                type="date"
                value={formData.endDate ? formData.endDate.substring(0, 10) : ""}
                onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value ? new Date(e.target.value).toISOString() : "" }))}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#F9FAFB", "& fieldset": { borderColor: "#F3F4F6" } },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="#374151" fontWeight={600} mb={1}>
                Expiry Date
              </Typography>
              <TextField
                fullWidth
                type="date"
                value={formData.expiryDate ? formData.expiryDate.substring(0, 10) : ""}
                onChange={(e) => setFormData(prev => ({ ...prev, expiryDate: e.target.value ? new Date(e.target.value).toISOString() : "" }))}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#F9FAFB", "& fieldset": { borderColor: "#F3F4F6" } },
                }}
              />
            </Grid>

            {/* Cover Image */}
            <Grid item xs={12}>
              <Typography variant="body2" color="#374151" fontWeight={600} mb={1}>
                Cover Image
              </Typography>
              <Box
                component={formData.images?.length > 0 ? "div" : "label"}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  py: 6,
                  border: "2px dashed #E5E7EB",
                  borderRadius: "12px",
                  cursor: formData.images?.length > 0 ? "default" : "pointer",
                  bgcolor: "#F9FAFB",
                  transition: "all 0.2s",
                  "&:hover": { borderColor: "#9CA3AF", bgcolor: "#F3F4F6" },
                  position: "relative"
                }}
              >
                {!formData.images?.length && <input hidden type="file" accept="image/*" onChange={handleImageUpload} />}
                {uploadingImage ? (
                  <CircularProgress size={30} sx={{ color: themeColor }} />
                ) : formData.images?.length > 0 ? (
                  <>
                    <Box
                      component="img"
                      src={formData.images[0].url}
                      alt="Cover preview"
                      sx={{ width: "100%", height: 160, objectFit: "contain", borderRadius: "8px" }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setFormData(prev => ({ ...prev, images: [] })); }}
                      sx={{ position: "absolute", top: 8, right: 8, bgcolor: "rgba(255,255,255,0.8)", "&:hover": { bgcolor: "white" } }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <UploadIcon sx={{ fontSize: 36, color: "#9CA3AF", mb: 1 }} />
                    <Typography variant="body2" color="#6B7280" fontWeight={500}>
                      Drop image here or click to upload
                    </Typography>
                    <Typography variant="caption" color="#9CA3AF" mt={0.5}>
                      PNG, JPG up to 5MB
                    </Typography>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: "1px solid #F3F4F6", justifyContent: "space-between" }}>
          <Button
            variant="outlined"
            onClick={handleCloseDialog}
            sx={{
              borderRadius: "8px",
              borderColor: "#E5E7EB",
              color: "#374151",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 1,
              "&:hover": { borderColor: "#D1D5DB", bgcolor: "#F9FAFB" },
            }}
          >
            Cancel
          </Button>
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              sx={{
                borderRadius: "8px",
                bgcolor: "#F9FAFB",
                color: "#374151",
                boxShadow: "none",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                py: 1,
                "&:hover": { bgcolor: "#F3F4F6", boxShadow: "none" },
              }}
            >
              Save Draft
            </Button>
            <Button
              variant="contained"
              sx={{
                borderRadius: "8px",
                bgcolor: "#F9FAFB",
                color: "#374151",
                boxShadow: "none",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                py: 1,
                "&:hover": { bgcolor: "#F3F4F6", boxShadow: "none" },
              }}
            >
              Preview
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                borderRadius: "8px",
                bgcolor: themeColor,
                color: "#fff",
                boxShadow: "none",
                textTransform: "none",
                fontWeight: 600,
                px: 4,
                py: 1,
                "&:hover": { bgcolor: themeColorHover, boxShadow: "none" },
              }}
            >
              Publish
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ================= VIEW DIALOG ================= */}
      <Dialog
        open={openViewDialog}
        onClose={handleCloseViewDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            bgcolor: "#fff",
            p: 3,
            minHeight: "90vh",
          },
        }}
      >
        {selectedEvent && (
          <>
            {/* Header */}
            <DialogTitle sx={{ p: 0, mb: 2, display: "flex", flexDirection: "column" }}>
              <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={1}>
                  <IconButton onClick={handleCloseViewDialog} sx={{ p: 0, mr: 1 }}>
                    <BackIcon />
                  </IconButton>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="#111827">
                      View Opportunity
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      You can view or edit the upcoming opportunities
                    </Typography>
                  </Box>
                </Box>
                {isEditingView ? (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setIsEditingView(false);
                      setViewFormData(buildViewFormData(selectedEvent));
                    }}
                    sx={{
                      borderRadius: "20px",
                      textTransform: "none",
                      fontWeight: 600,
                      px: 4,
                      py: 1,
                      color: "#6B7280",
                      borderColor: "#D1D5DB",
                      "&:hover": { borderColor: "#9CA3AF", bgcolor: "#F9FAFB" },
                    }}
                  >
                    Cancel
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={() => setIsEditingView(true)}
                    sx={{
                      borderRadius: "20px",
                      textTransform: "none",
                      fontWeight: 600,
                      px: 5,
                      py: 1.2,
                      background: "linear-gradient(90deg, #9B59F0 0%, #E07BAA 50%, #F0A070 100%)",
                      color: "#fff",
                      boxShadow: "none",
                      "&:hover": {
                        background: "linear-gradient(90deg, #8B45E0 0%, #D06B9A 50%, #E09060 100%)",
                        boxShadow: "0 4px 14px rgba(155, 89, 240, 0.3)",
                      },
                    }}
                  >
                    Edit
                  </Button>
                )}
              </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0, overflowX: "hidden" }}>
              {/* Mode Chip */}
              <Box sx={{ mb: 3, ml: 5 }}>
                <Chip
                  label={viewFormData.mode || selectedEvent.mode}
                  sx={{
                    bgcolor: "#6366F1",
                    color: "#fff",
                    fontWeight: 600,
                    borderRadius: "20px",
                    px: 2,
                    fontSize: "0.85rem",
                  }}
                />
              </Box>

              <Grid container spacing={6} sx={{ ml: 0, width: "100%" }}>
                {/* LEFT COLUMN - EVENT DETAILS */}
                <Grid item xs={12} md={5} sx={{ pt: 0 }}>
                  <Box display="flex" flexDirection="column" gap={2.5}>

                    {/* Brand Logo */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Brand Logo</Typography>
                      {selectedEvent.images?.[0] ? (
                        <Box component="img" src={selectedEvent.images[0].url} alt="Logo" sx={{ width: 60, height: 60, borderRadius: "12px", objectFit: "cover", border: "2px solid #E5E7EB" }} />
                      ) : (
                        <Box sx={{ width: 60, height: 60, borderRadius: "12px", bgcolor: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Typography color="text.disabled" variant="caption">N/A</Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Brand Name */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Brand Name</Typography>
                      {isEditingView ? (
                        <TextField fullWidth name="title" value={viewFormData.title} onChange={handleViewInputChange} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                      ) : (
                        <Typography variant="body1" fontWeight={600}>{selectedEvent.title}</Typography>
                      )}
                    </Box>

                    {/* Opportunity Title */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Opportunity Title</Typography>
                      {isEditingView ? (
                        <TextField fullWidth name="title" value={viewFormData.title} onChange={handleViewInputChange} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                      ) : (
                        <Typography variant="body1" fontWeight={600}>{selectedEvent.title}</Typography>
                      )}
                    </Box>

                    {/* Opportunity Category */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Opportunity Category</Typography>
                      {isEditingView ? (
                        <FormControl fullWidth size="small">
                          <Select name="categories" multiple value={viewFormData.categories} onChange={handleViewCategoryChange}
                            renderValue={(sel) => sel.length === 0 ? <Typography color="text.secondary">Select</Typography> : sel.join(", ")}
                            sx={{ borderRadius: "12px" }}>
                            {availableCategories.map((cat) => (<MenuItem key={cat} value={cat}>{cat}</MenuItem>))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body1" fontWeight={600}>{selectedEvent.categories?.join(", ") || "-"}</Typography>
                      )}
                    </Box>

                    {/* Details / Description */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Details / Description</Typography>
                      {isEditingView ? (
                        <TextField fullWidth multiline rows={3} name="description" value={viewFormData.description} onChange={handleViewInputChange} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                      ) : (
                        <Typography variant="body1" fontWeight={600} sx={{ whiteSpace: "pre-wrap" }}>{selectedEvent.description}</Typography>
                      )}
                    </Box>

                    {/* Opportunity Price Range Min. */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Opportunity Price Range Min.</Typography>
                      {isEditingView ? (
                        <Box display="flex" gap={1}>
                          <FormControl sx={{ flex: 1 }} size="small">
                            <Select name="priceCurrencyMin" value={viewFormData.priceCurrencyMin} onChange={handleViewInputChange} sx={{ borderRadius: "12px" }}>
                              <MenuItem value="Currency">Currency</MenuItem>
                              {activeCurrencies.map((c) => (
                                <MenuItem key={c._id} value={c.value}>{c.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField placeholder="Min Price" name="priceRangeMin" value={viewFormData.priceRangeMin} onChange={handleViewInputChange} size="small" sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                        </Box>
                      ) : (
                        <Typography variant="body1" fontWeight={700}>
                          {selectedEvent.priceRangeMin != null && selectedEvent.priceRangeMin !== ""
                            ? `Min. ${selectedEvent.priceCurrencyMin && selectedEvent.priceCurrencyMin !== "Currency" ? selectedEvent.priceCurrencyMin + " " : ""}${selectedEvent.priceRangeMin}${selectedEvent.priceRangeMax != null && selectedEvent.priceRangeMax !== "" ? ` Ã¢â‚¬â€œ Max. ${selectedEvent.priceCurrencyMax && selectedEvent.priceCurrencyMax !== "Currency" ? selectedEvent.priceCurrencyMax + " " : ""}${selectedEvent.priceRangeMax}` : ""}`
                            : `Min. ${selectedEvent.price || "-"}`}
                        </Typography>
                      )}
                    </Box>

                    {/* Discount Range */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Discount Range</Typography>
                      {isEditingView ? (
                        <TextField fullWidth placeholder="e.g. 20%" name="discountRange" value={viewFormData.discountRange} onChange={handleViewInputChange} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                      ) : (
                        <Typography variant="body1" fontWeight={600}>{selectedEvent.discountRange || "-"}</Typography>
                      )}
                    </Box>

                    {/* Final Price */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Final Price</Typography>
                      {isEditingView ? (
                        <TextField fullWidth placeholder="e.g. INR 20000" name="finalPrice" value={viewFormData.finalPrice} InputProps={{ readOnly: true }} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                      ) : (
                        <Typography variant="body1" fontWeight={600}>{selectedEvent.finalPrice || selectedEvent.price || "-"}</Typography>
                      )}
                    </Box>

                    {/* Opportunity Booking Link */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Opportunity Booking Link</Typography>
                      {isEditingView ? (
                        <TextField fullWidth placeholder="Enter URL" name="bookingLink" value={viewFormData.bookingLink} onChange={handleViewInputChange} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                      ) : (
                        <Typography variant="body1" fontWeight={600}>{selectedEvent.bookingLink ? `URI: ${selectedEvent.bookingLink}` : "-"}</Typography>
                      )}
                    </Box>

                    {/* Manage Offer Type */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Manage Offer Type</Typography>
                      {isEditingView ? (
                        <FormControl fullWidth size="small">
                          <Select name="manageEventType" value={viewFormData.manageEventType} onChange={handleViewInputChange} sx={{ borderRadius: "12px" }}>
                            {activeEventTypes.map((t) => (
                              <MenuItem key={t._id} value={t.value}>{t.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body1" fontWeight={600}>{selectedEvent.manageEventType || selectedEvent.eventType || "-"}</Typography>
                      )}
                    </Box>

                    {/* Opportunity Start Date */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Opportunity Start Date</Typography>
                      {isEditingView ? (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DateTimeField
                            value={viewFormData.startDate ? dayjs(viewFormData.startDate) : null}
                            onChange={(newValue) => {
                              if (newValue && typeof newValue?.isValid === "function" && newValue.isValid()) {
                                setViewFormData((prev) => ({ ...prev, startDate: newValue.toISOString() }));
                              }
                            }}
                            format="DD-MM-YYYY hh:mm A"
                            slotProps={{ textField: { fullWidth: true, size: "small", sx: { "& .MuiOutlinedInput-root": { borderRadius: "12px" } } } }}
                          />
                        </LocalizationProvider>
                      ) : (
                        <Typography variant="body1" fontWeight={600}>
                          {dayjs(selectedEvent.startDate).isValid() ? dayjs(selectedEvent.startDate).format("DD-MM-YYYY") : "-"}
                        </Typography>
                      )}
                    </Box>

                    {/* Opportunity End Date */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Opportunity End Date</Typography>
                      {isEditingView ? (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DateTimeField
                            value={viewFormData.endDate ? dayjs(viewFormData.endDate) : null}
                            onChange={(newValue) => {
                              if (newValue && typeof newValue?.isValid === "function" && newValue.isValid()) {
                                setViewFormData((prev) => ({ ...prev, endDate: newValue.toISOString() }));
                              }
                            }}
                            format="DD-MM-YYYY hh:mm A"
                            slotProps={{ textField: { fullWidth: true, size: "small", sx: { "& .MuiOutlinedInput-root": { borderRadius: "12px" } } } }}
                          />
                        </LocalizationProvider>
                      ) : (
                        <Typography variant="body1" fontWeight={600}>
                          {dayjs(selectedEvent.endDate).isValid() ? dayjs(selectedEvent.endDate).format("DD-MM-YYYY") : "-"}
                        </Typography>
                      )}
                    </Box>

                  </Box>
                </Grid>

                {/* RIGHT COLUMN - MEDIA & MAP */}
                <Grid item xs={12} md={7} sx={{ pt: 0 }}>
                  <Box display="flex" flexDirection="column" gap={3}>

                    {/* Preview */}
                    <Box>
                      <Typography variant="body2" color="#111827" fontWeight={600} mb={1} textAlign="center">Preview</Typography>
                      <Box sx={{ width: "100%", height: "280px", borderRadius: "16px", overflow: "hidden", border: "1px solid #E5E7EB", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
                        {(() => {
                          const primary = selectedEvent.images?.find(img => img.isPrimary) || selectedEvent.images?.[0];
                          if (!primary) return (
                            <Box display="flex" alignItems="center" justifyContent="center" height="100%" bgcolor="#F9FAFB">
                              <Typography color="text.disabled">No Image</Typography>
                            </Box>
                          );
                          return primary.type === "video" || getMediaType(primary.url) === "video" ? (
                            <video src={primary.url} controls style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : (
                            <img src={primary.url} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          );
                        })()}
                      </Box>
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem", display: "block", mt: 1, textAlign: "center" }}>
                        This How the Main Poster looks like on<br />the Thumbnail of the Opportunity post
                      </Typography>
                    </Box>

                    {/* Gallery Thumbnails */}
                    {selectedEvent.images && selectedEvent.images.length > 0 && (
                      <Box>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          {selectedEvent.images.map((img, i) => (
                            <Box key={i} sx={{ width: "100px", height: "140px", borderRadius: "12px", overflow: "hidden", border: img.isPrimary ? "3px solid #6366F1" : "1px solid #E5E7EB" }}>
                              {img.type === "video" || getMediaType(img.url) === "video" ? (
                                <video src={img.url} muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              ) : (
                                <img src={img.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              )}
                            </Box>
                          ))}
                        </Box>
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.6rem", display: "block", mt: 1 }}>
                          Drag and swap for the main poster thumbnail
                        </Typography>
                      </Box>
                    )}

                    {/* Map Location Search */}
                    <Box>
                      <Typography variant="body2" color="#111827" fontWeight={600} mb={1}>Map location search</Typography>
                      {isEditingView ? (
                        <Autocomplete
                          freeSolo
                          options={locationSuggestions}
                          getOptionLabel={(option) => typeof option === "string" ? option : option.label || ""}
                          loading={locationSearchLoading}
                          filterOptions={(x) => x}
                          inputValue={viewFormData.mapLocationSearch}
                          onInputChange={(event, newInputValue, reason) => {
                            if (reason === "input") {
                              setViewFormData((prev) => ({ ...prev, mapLocationSearch: newInputValue }));
                              handleLocationSearch(newInputValue);
                            }
                          }}
                          onChange={(event, newValue) => {
                            if (newValue && typeof newValue !== "string") {
                              setViewFormData((prev) => ({
                                ...prev,
                                mapLocationSearch: newValue.label,
                                mapSearchTrigger: newValue.label,
                                location: {
                                  city: newValue.address?.address?.city || newValue.address?.address?.town || newValue.address?.address?.county || "",
                                  country: newValue.address?.address?.country || "",
                                  fullAddress: newValue.label,
                                  lat: newValue.lat,
                                  lng: newValue.lng,
                                },
                              }));
                            }
                          }}
                          renderInput={(params) => (
                            <TextField {...params} fullWidth placeholder="Search location..." size="small"
                              InputProps={{ ...params.InputProps, startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: "#9CA3AF" }} /></InputAdornment>) }}
                              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#F3F4F6", "& fieldset": { border: "none" } } }}
                            />
                          )}
                        />
                      ) : (
                        <Typography variant="body1" fontWeight={600}>
                          {selectedEvent.location?.fullAddress || `${selectedEvent.location?.city || ""}, ${selectedEvent.location?.country || ""}`}
                        </Typography>
                      )}
                    </Box>

                    {/* Map URL */}
                    <Box>
                      <Typography variant="body2" color="#111827" fontWeight={600} mb={1}>Map URL</Typography>
                      {isEditingView ? (
                        <TextField fullWidth placeholder="Enter URL" name="mapUrl" value={viewFormData.mapUrl} onChange={handleViewInputChange} size="small"
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#F3F4F6", "& fieldset": { border: "none" } } }}
                        />
                      ) : (
                        <Typography variant="body1" fontWeight={600}>{selectedEvent.mapUrl ? `URI: ${selectedEvent.mapUrl}` : "-"}</Typography>
                      )}
                    </Box>

                    {/* Map Embed */}
                    <Box sx={{ width: "100%", height: "300px", borderRadius: "16px", overflow: "hidden", border: "2px solid #8B5CF6", boxShadow: "0 4px 12px rgba(139, 92, 246, 0.2)" }}>
                      {isEditingView ? (
                        <LocationPicker
                          onLocationSelect={(payload) => {
                            setViewFormData((prev) => ({
                              ...prev,
                              location: {
                                city: payload.address?.address?.city || payload.address?.address?.county,
                                country: payload.address?.address?.country,
                                fullAddress: payload.address?.display_name,
                                lat: payload.lat,
                                lng: payload.lng,
                              },
                            }));
                          }}
                          locationValue={viewFormData.location?.lat && viewFormData.location?.lng ? `${viewFormData.location.lat},${viewFormData.location.lng}` : ""}
                          searchQuery={viewFormData.mapSearchTrigger}
                        />
                      ) : (
                        (() => {
                          const lat = selectedEvent.location?.geo?.coordinates?.[1];
                          const lng = selectedEvent.location?.geo?.coordinates?.[0];
                          if (lat && lng) {
                            return (
                              <iframe
                                title="Opportunity Location"
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                src={`https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.01},${lat - 0.01},${lng + 0.01},${lat + 0.01}&layer=mapnik&marker=${lat},${lng}`}
                              />
                            );
                          }
                          return (
                            <Box display="flex" alignItems="center" justifyContent="center" height="100%" bgcolor="#F9FAFB">
                              <Typography color="text.disabled">No location data</Typography>
                            </Box>
                          );
                        })()
                      )}
                    </Box>

                  </Box>
                </Grid>
              </Grid>
            </DialogContent>

            {/* Bottom Action Button */}
            <DialogActions sx={{ p: 0, mt: 4 }}>
              <Button
                variant="contained"
                onClick={isEditingView ? handleSaveEditedEvent : handleCloseViewDialog}
                fullWidth
                disabled={savingEvent}
                sx={{
                  bgcolor: "#6366F1",
                  "&:hover": { bgcolor: "#4F46E5" },
                  borderRadius: "12px",
                  py: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "1.1rem",
                }}
              >
                {savingEvent ? <CircularProgress size={24} color="inherit" /> : isEditingView ? "Save Opportunity" : "Close"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ================= DELETE CONFIRMATION DIALOG ================= */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 4,
            textAlign: "center",
          },
        }}
      >
        <Typography variant="h6" fontWeight={700} color="#111827" gutterBottom>
          Please double-check before deleting this opportunity
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          This action can be undone, but all opportunity-related data will be removed
          from the mobile application.
        </Typography>
        <Box display="flex" justifyContent="center" gap={2}>
          <Button
            variant="contained"
            onClick={handleDeleteOpportunity}
            sx={{
              px: 4,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#C62828",
              "&:hover": { bgcolor: "#B71C1C" },
            }}
          >
            Delete
          </Button>
          <Button
            variant="outlined"
            onClick={handleCloseDeleteDialog}
            sx={{
              px: 4,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
        </Box>
      </Dialog>

      {/* ================= ADD CATEGORY DIALOG ================= */}
      <Dialog
        open={openAddCategoryDialog}
        onClose={() => setOpenAddCategoryDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 2 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Add New {settingsCategoryType === "Formal Categories" ? "Formal" : "Casual"} Category
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              fullWidth
              size="small"
              label="Category Name"
              placeholder="e.g. Graphic Design"
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label="Emoji / Icon"
              placeholder="e.g. 🎨 or 💼"
              value={newCatIcon}
              onChange={(e) => setNewCatIcon(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenAddCategoryDialog(false)}
            sx={{ textTransform: "none", color: "#6B7280" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateNewCategory}
            sx={{
              bgcolor: settingsCategoryType === "Formal Categories" ? "#069480" : "#6D53F4",
              "&:hover": {
                bgcolor: settingsCategoryType === "Formal Categories" ? "#057A6A" : "#5A3FE0",
              },
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              px: 3,
            }}
          >
            Add Category
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= ADD/EDIT OPPORTUNITY TYPE DIALOG ================= */}
      <Dialog
        open={openTypeDialog}
        onClose={() => !typeSubmitting && setOpenTypeDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" fontWeight={700} color="#111827">
            {editingType ? "Edit Opportunity Type" : "Add Opportunity Type"}
          </Typography>
          <IconButton size="small" onClick={() => setOpenTypeDialog(false)} disabled={typeSubmitting}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box display="flex" flexDirection="column" gap={2.5} mt={0.5}>
            <TextField
              fullWidth
              size="small"
              label="Type Name"
              placeholder="e.g. Online, Contract, Part-Time"
              value={typeName}
              onChange={(e) => setTypeName(e.target.value)}
              required
              disabled={typeSubmitting}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "8px" }
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Description"
              placeholder="e.g. Fully remote, no physical presence needed"
              multiline
              rows={3}
              value={typeDescription}
              onChange={(e) => setTypeDescription(e.target.value)}
              disabled={typeSubmitting}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "8px" }
              }}
            />
            {/* Active / Inactive Status Switch */}
            <Box
              p={1.5}
              bgcolor="#F9FAFB"
              borderRadius="10px"
              border="1px solid #F3F4F6"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="body2" fontWeight={600} color="#111827">
                  Status
                </Typography>
                <Typography variant="caption" color="#6B7280">
                  {typeStatus ? "Active and available for selection" : "Inactive / hidden from dropdowns"}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip
                  label={typeStatus ? "Active" : "Inactive"}
                  size="small"
                  sx={{
                    bgcolor: typeStatus ? "#DCFCE7" : "#F3F4F6",
                    color: typeStatus ? "#15803D" : "#6B7280",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    borderRadius: "9999px",
                    height: "22px",
                  }}
                />
                <Switch
                  size="small"
                  checked={typeStatus}
                  onChange={(e) => setTypeStatus(e.target.checked)}
                  disabled={typeSubmitting}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#6366F1" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#6366F1" },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setOpenTypeDialog(false)}
            disabled={typeSubmitting}
            sx={{ textTransform: "none", color: "#6B7280", borderRadius: "8px", fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveType}
            disabled={typeSubmitting}
            startIcon={typeSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              bgcolor: "#6366F1",
              "&:hover": { bgcolor: "#4F46E5" },
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              px: 3,
              boxShadow: "none",
            }}
          >
            {typeSubmitting ? "Saving..." : (editingType ? "Save Changes" : "Add Opportunity Type")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= DELETE OPPORTUNITY TYPE CONFIRMATION DIALOG ================= */}
      <Dialog
        open={openDeleteTypeDialog}
        onClose={() => !deletingType && setOpenDeleteTypeDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: "#FEE2E2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#DC2626",
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </Box>
          <Typography variant="h6" fontWeight={700} color="#111827">
            Delete Opportunity Type
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="#6B7280" mt={1}>
            Are you sure you want to delete{" "}
            <strong style={{ color: "#111827" }}>
              "{typeToDelete?.label || typeToDelete?.type || typeToDelete?.value || "this opportunity type"}"
            </strong>
            ? This action cannot be undone and will permanently remove it from the database.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setOpenDeleteTypeDialog(false)}
            disabled={deletingType}
            sx={{ textTransform: "none", color: "#6B7280", borderRadius: "8px", fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDeleteType}
            disabled={deletingType}
            startIcon={deletingType ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              bgcolor: "#EF4444",
              "&:hover": { bgcolor: "#DC2626" },
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              px: 2.5,
              boxShadow: "none",
            }}
          >
            {deletingType ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OpportunityManagement;


